"use client";
import React from "react";
import { Menu, MenuItem } from "@mui/material";
import { DashboardCustomize } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import {
  Settings,
  Users,
  UserPlus,
  Cake,
  Award,
  UserCheck,
  Clock,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Sparkles,
  ClipboardCheck,
  Timer,
} from "lucide-react";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { resolveOrganizationFromUserData } from "../../lib/organizationSession";
import "./dashboardStyles.css";

const EMS_CHART_COLORS = [
  "var(--ems-chart-1)",
  "var(--ems-chart-2)",
  "var(--ems-chart-3)",
  "var(--ems-chart-4)",
  "var(--ems-chart-5)",
  "var(--ems-chart-6)",
];

const CHART_PLOT_HEIGHT = 300;

const CHART_GRID_PROPS = {
  strokeDasharray: "3 3",
  stroke: "var(--ems-border)",
  vertical: true,
  horizontal: true,
};

const CHART_AXIS_TICK = { fontSize: 11, fill: "var(--ems-muted)" };

const CHART_TOOLTIP_BOX_STYLE = {
  background: "var(--ems-surface)",
  border: "1px solid var(--ems-border)",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 13,
  fontFamily: "Manrope, sans-serif",
  color: "var(--ems-text)",
  boxShadow: "var(--ems-shadow-md)",
};

/** Theme-aware Recharts tooltip (readable in light + dark mode). */
function DashboardChartTooltip({
  active,
  payload,
  label,
  valueLabel = "Employees",
  showShortLabels = false,
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0] || {};
  const row = point.payload || {};
  const value = point.value ?? row.value ?? 0;
  const fullName = row.fullName || row.name || point.name || label || "";
  const shortName = row.name || point.name || label || "";
  const title =
    showShortLabels &&
    fullName &&
    shortName &&
    String(fullName) !== String(shortName)
      ? `${fullName} (${shortName})`
      : fullName || shortName || "—";

  return (
    <div style={CHART_TOOLTIP_BOX_STYLE}>
      <div style={{ fontWeight: 650, marginBottom: 4, color: "var(--ems-text)" }}>
        {title}
      </div>
      <div style={{ color: "var(--ems-text-soft)" }}>
        {valueLabel} : {Number(value || 0).toLocaleString("en-IN")}
      </div>
    </div>
  );
}

const AVATAR_TONES = ["#6b5ae0", "#14b8a6", "#f59e0b", "#3b82f6", "#ec4899", "#0ea5e9"];

function greetForHour(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function personDisplayName(p) {
  if (!p) return "Unknown";
  const composed = [p.first_name, p.middle_name, p.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    composed ||
    p.Full_Name ||
    p.full_name ||
    p.Name ||
    p.name ||
    p.employee_name ||
    p.intern_name ||
    p.Employee_Name ||
    "Unknown"
  );
}

function personInitials(name) {
  const parts = String(name || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return (parts[0] || "U").slice(0, 2).toUpperCase();
}

function resolveProfileImageUrl(person, folder = "employees") {
  const raw =
    person?.profile_image_url ||
    person?.Profile_Image_Url ||
    person?.profile_image ||
    person?.Profile_Image ||
    person?.photo_url ||
    person?.Photo ||
    person?.org_persons?.person?.profile_image_url ||
    "";

  if (!raw || typeof raw !== "string") return "";
  const value = raw.trim();
  if (!value) return "";

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  if (
    value.startsWith("employees/") ||
    value.startsWith("persons/") ||
    value.startsWith("interns/") ||
    value.startsWith("freelancers/")
  ) {
    return `${MAIN_URL}/storage/app/public/${value}`;
  }

  if (value.startsWith("/storage/")) {
    return `${MAIN_URL}${value}`;
  }

  return `${MAIN_URL}/storage/app/public/${folder}/${value}`;
}

function PersonAvatar({ person, name, toneIndex = 0, folder = "employees" }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const photoUrl = resolveProfileImageUrl(person, folder);
  const showPhoto = Boolean(photoUrl) && !imgFailed;
  const label = name || personDisplayName(person);

  React.useEffect(() => {
    setImgFailed(false);
  }, [photoUrl]);

  if (showPhoto) {
    return (
      <span className="hd-avatar hd-avatar-photo">
        <img
          src={photoUrl}
          alt={label}
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className="hd-avatar"
      style={{ background: AVATAR_TONES[toneIndex % AVATAR_TONES.length] }}
      aria-hidden="true"
    >
      {personInitials(label)}
    </span>
  );
}

function personRole(p) {
  if (typeof p?.Designation === "string" && p.Designation.trim()) {
    return p.Designation.trim();
  }
  if (p?.Designation && typeof p.Designation === "object") {
    return (
      p.Designation.designation_name ||
      p.Designation.name ||
      p.Designation.Designation ||
      "—"
    );
  }
  return (
    p?.designation_name ||
    p?.designation?.designation_name ||
    p?.designation?.name ||
    p?.Role ||
    p?.role ||
    "—"
  );
}

function personDepartment(p) {
  if (typeof p?.Department === "string" && p.Department.trim()) return p.Department;
  if (Array.isArray(p?.Department) && p.Department[0]) {
    return (
      p.Department[0]?.department?.department_name ||
      p.Department[0]?.name ||
      "—"
    );
  }
  if (Array.isArray(p?.department_location) && p.department_location[0]) {
    return (
      p.department_location[0]?.department?.department_name ||
      "—"
    );
  }
  return (
    p?.department?.department_name ||
    p?.department_name ||
    p?.Department_Name ||
    "—"
  );
}

function personJoinDate(p) {
  return (
    p?.Date_of_Joining ||
    p?.date_of_joining ||
    p?.internship_start_date ||
    p?.Internship_Start_Date ||
    null
  );
}

function formatShortDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value).slice(0, 11);
  }
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeValue(val) {
  if (val == null || val === "") return "—";
  if (typeof val === "string") {
    const s = val.trim();
    if (!s || ["null", "undefined", "na", "n/a"].includes(s.toLowerCase())) return "—";
    if (/^\d{1,2}:\d{2}/.test(s)) return s.slice(0, 5);
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return s;
  }
  return "—";
}

function attendanceRowForPerson(p) {
  const today =
    p?.attendance_today ||
    p?.Attendance?.[0] ||
    p?.attendance?.[0] ||
    null;

  const checkIn = formatTimeValue(
    today?.check_in ||
      today?.CheckIn ||
      today?.clock_in ||
      today?.ClockIn ||
      today?.first_check_in ||
      today?.time_in ||
      p?.check_in ||
      p?.clock_in
  );
  const checkOut = formatTimeValue(
    today?.check_out ||
      today?.CheckOut ||
      today?.clock_out ||
      today?.ClockOut ||
      today?.time_out ||
      p?.check_out ||
      p?.clock_out
  );

  const rawStatus = String(
    today?.status || today?.Status || p?.today_status || p?.AttendanceStatus || ""
  ).toLowerCase();

  let status = "Absent";
  if (rawStatus.includes("late")) status = "Late";
  else if (
    rawStatus.includes("present") ||
    rawStatus === "in" ||
    rawStatus.includes("checked-in") ||
    checkIn !== "—"
  ) {
    status = "Present";
  } else if (rawStatus.includes("absent") || rawStatus === "out") {
    status = "Absent";
  }

  return {
    id: p?.employee_id || p?.intern_id || p?.freelance_employee_id || personDisplayName(p),
    name: personDisplayName(p),
    person: p,
    checkIn,
    checkOut,
    status,
  };
}

function WorkforceStat({ tone, icon, label, value, onClick }) {
  const clickable = typeof onClick === "function";
  const className = clickable ? "hd-stat" : "hd-stat hd-stat-static";
  const content = (
    <>
      <div className={`hd-stat-icon ${tone}`}>{icon}</div>
      <div className="hd-stat-label">{label}</div>
      <div className="hd-stat-value">{value}</div>
    </>
  );
  if (!clickable) {
    return <div className={className}>{content}</div>;
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  );
}

function personOrgPersonId(p) {
  return (
    p?.organization_person_id ||
    p?.Organization_Person_Id ||
    p?.org_persons?.organization_person_id ||
    p?.id ||
    null
  );
}

function buildTodayClockInTimeLogsUrl(basePath = "/attendance/time-logs") {
  const params = new URLSearchParams({
    page: "1",
    pageSize: "10",
    sortBy: "organization_person_attendance_timelog_id",
    sortOrder: "desc",
    filter_attendance_date: "",
    filter_attendance_date_operator: "today",
    filter_attendance_log_type: "Clock In",
    filter_attendance_log_type_operator: "equals",
  });
  return `${basePath}?${params.toString()}`;
}

const TODAY_CLOCK_IN_FILTERS = [
  { field: "attendance_date", operator: "today", value: null },
  { field: "attendance_log_type", operator: "equals", value: "Clock In" },
];

async function fetchTodayClockInLogs(orgId, filteredPath) {
  const response = await fetch(
    `${MAIN_URL}/api/organizations/${orgId}/${filteredPath}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        filters: TODAY_CLOCK_IN_FILTERS,
        page: 1,
        pageSize: 100,
        sort: {
          field: "attendance_log_time",
          direction: "asc",
        },
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = await response.json();
  if (Array.isArray(result)) return result;
  if (result?.success && Array.isArray(result.data)) return result.data;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

async function fetchWorkShifts(orgId) {
  const response = await fetch(
    `${MAIN_URL}/api/organizations/${orgId}/workshift?per_page=999`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Accept: "application/json",
      },
    }
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const result = await response.json();
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.workshifts?.data)) return result.workshifts.data;
  if (Array.isArray(result?.workshifts)) return result.workshifts;
  if (Array.isArray(result?.Workshifts?.data)) return result.Workshifts.data;
  if (Array.isArray(result?.Workshifts)) return result.Workshifts;
  return [];
}

async function fetchLeaveRows(orgId, filteredPath, filters, pageSize = 200) {
  const response = await fetch(
    `${MAIN_URL}/api/organizations/${orgId}/${filteredPath}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        filters,
        page: 1,
        pageSize,
        sort: {
          field: "organization_person_leave_id",
          direction: "desc",
        },
      }),
    }
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const result = await response.json();
  if (Array.isArray(result)) return result;
  if (result?.success && Array.isArray(result.data)) return result.data;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

/** Prefer windowed filter; if metadata rejects it, fall back to recent unfiltered rows. */
async function fetchLeaveRowsForDashboard(orgId, filteredPath, todayYmd) {
  const windowFilters = buildRecentLeaveWindowApiFilters(todayYmd);
  try {
    const rows = await fetchLeaveRows(orgId, filteredPath, windowFilters, 500);
    if (Array.isArray(rows) && rows.length > 0) return rows;
  } catch {
    // fall through
  }
  try {
    return await fetchLeaveRows(orgId, filteredPath, [], 300);
  } catch {
    return [];
  }
}

/** Parse "HH:mm", "HH:mm:ss", or "09:00 AM" → minutes from midnight. */
function parseTimeToMinutes(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    // Already minutes, or excel-style fraction of day
    if (value >= 0 && value < 24) return Math.round(value * 60);
    if (value >= 0 && value < 24 * 60) return Math.round(value);
    return null;
  }
  const s = String(value).trim();
  if (!s) return null;

  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = Number(ampm[2]);
    const mer = ampm[3].toUpperCase();
    if (mer === "PM" && h < 12) h += 12;
    if (mer === "AM" && h === 12) h = 0;
    return h * 60 + m;
  }

  const parts = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (parts) {
    return Number(parts[1]) * 60 + Number(parts[2]);
  }
  return null;
}

function formatMinutesAsShiftLabel(startMin, endMin) {
  const toLabel = (mins) => {
    if (mins == null) return "";
    const h24 = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const d = new Date();
    d.setHours(h24, m, 0, 0);
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  if (startMin == null) return "—";
  if (endMin == null) return toLabel(startMin);
  return `${toLabel(startMin)}–${toLabel(endMin)}`;
}

function personWorkShiftName(p) {
  return String(
    p?.Work_Shift ||
      p?.work_shift_name ||
      p?.workshift?.work_shift_name ||
      p?.work_shift?.work_shift_name ||
      ""
  ).trim();
}

function buildWorkShiftLookup(shifts) {
  const byName = new Map();
  const byId = new Map();
  (shifts || []).forEach((s) => {
    const name = String(s?.work_shift_name || s?.Work_Shift || "").trim();
    const startMin = parseTimeToMinutes(
      s?.work_shift_start_time || s?.start_time
    );
    const endMin = parseTimeToMinutes(s?.work_shift_end_time || s?.end_time);
    const entry = {
      name,
      startMin,
      endMin,
      label: formatMinutesAsShiftLabel(startMin, endMin),
    };
    if (name) byName.set(name.toLowerCase(), entry);
    const id =
      s?.organization_work_shift_id ?? s?.work_shift_id ?? s?.id ?? null;
    if (id != null) byId.set(String(id), entry);
  });
  return { byName, byId };
}

function resolvePersonShift(p, shiftLookup) {
  const byName = shiftLookup?.byName || shiftLookup;
  const byId = shiftLookup?.byId;
  const shiftId =
    p?.organization_work_shift_id ??
    p?.work_shift_id ??
    p?.workshift?.organization_work_shift_id ??
    null;
  if (shiftId != null && byId?.has(String(shiftId))) {
    return byId.get(String(shiftId));
  }
  const shiftName = personWorkShiftName(p);
  if (shiftName && byName?.has?.(shiftName.toLowerCase())) {
    return byName.get(shiftName.toLowerCase());
  }
  return null;
}

function collectClockedInPersonKeys(logs) {
  const keys = new Set();
  (logs || []).forEach((log) => {
    const id = log?.organization_person_id;
    if (id != null) keys.add(`id:${id}`);
    const code = String(log?.employee_code || log?.intern_code || "")
      .trim()
      .toLowerCase();
    if (code) keys.add(`code:${code}`);
  });
  return keys;
}

function collectOnLeavePersonKeys(leaveRows) {
  const keys = new Set();
  (leaveRows || []).forEach((row) => {
    const id = row?.organization_person_id;
    if (id != null) keys.add(`id:${id}`);
    const code = String(row?.employee_code || row?.intern_code || "")
      .trim()
      .toLowerCase();
    if (code) keys.add(`code:${code}`);
  });
  return keys;
}

function personMatchKeys(p) {
  const keys = [];
  const id = personOrgPersonId(p);
  if (id != null) keys.push(`id:${id}`);
  const code = String(
    p?.employee_code || p?.intern_code || p?.person_code || p?.Employee_Code || p?.Intern_Code || ""
  )
    .trim()
    .toLowerCase();
  if (code) keys.push(`code:${code}`);
  return keys;
}

function keysIntersect(personKeys, set) {
  return personKeys.some((k) => set.has(k));
}

/** Local YYYY-MM-DD for comparing notice / last-working dates. */
function localYmd(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseYmdValue(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return localYmd(value);
  }
  const s = String(value).trim();
  if (!s) return null;
  // ISO / Y-m-d
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  // d-M-Y like 09-Sep-2025
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) return localYmd(parsed);
  return null;
}

/**
 * Keep active staff + exited staff still within notice/LWD.
 * Exclude exited (or status containing "exit") when notice/LWD/relieving is already past,
 * or when exited with no end date.
 */
function isActiveWorkforcePerson(person, todayYmd = localYmd()) {
  const status = String(
    person?.Status ||
      person?.employment_status ||
      person?.employment_status_name ||
      person?.Internship_Status ||
      person?.internship_status ||
      ""
  ).toLowerCase();

  if (!status.includes("exit")) return true;

  const endYmd =
    parseYmdValue(person?.notice_period_end) ||
    parseYmdValue(person?.last_working_date) ||
    parseYmdValue(person?.last_working_day) ||
    parseYmdValue(person?.relieving_date);

  if (!endYmd) return false;
  return endYmd >= todayYmd;
}

function collectPeopleMatchKeySet(people) {
  const keys = new Set();
  (people || []).forEach((p) => {
    personMatchKeys(p).forEach((k) => keys.add(k));
  });
  return keys;
}

function filterLogsForPeople(logs, people) {
  const peopleKeys = collectPeopleMatchKeySet(people);
  if (!peopleKeys.size) return [];
  return (logs || []).filter((log) => {
    const keys = [];
    if (log?.organization_person_id != null) {
      keys.push(`id:${log.organization_person_id}`);
    }
    const code = String(log?.employee_code || log?.intern_code || "")
      .trim()
      .toLowerCase();
    if (code) keys.push(`code:${code}`);
    return keys.some((k) => peopleKeys.has(k));
  });
}

/**
 * People who should appear in Today's Absentees:
 * - On approved leave today → show status only (no Mark actions)
 * - Shift start has passed, no clock-in, not on leave → Mark Attendance + Mark Leave
 */
function buildAbsentRows({
  people,
  shiftLookup,
  clockedKeys,
  leaveKeys,
  now = new Date(),
  personType = "employees",
  limit = 10,
}) {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const rows = [];

  (people || []).forEach((p) => {
    const keys = personMatchKeys(p);
    if (!keys.length) return;

    const onLeave = keysIntersect(keys, leaveKeys);
    const clockedIn = keysIntersect(keys, clockedKeys);
    const shiftName = personWorkShiftName(p);
    const shift = resolvePersonShift(p, shiftLookup);

    if (onLeave) {
      rows.push({
        id: personOrgPersonId(p) || personDisplayName(p),
        name: personDisplayName(p),
        person: p,
        personType,
        shiftLabel: shift?.label || shiftName || "—",
        shiftStartMin: shift?.startMin ?? null,
        status: "On Leave",
        showMarkAttendance: false,
        showMarkLeave: false,
      });
      return;
    }

    if (clockedIn) return;

    // Without a known shift start, only show after a default morning cutoff (9:00)
    const startMin = shift?.startMin ?? 9 * 60;
    if (nowMinutes < startMin) return;

    rows.push({
      id: personOrgPersonId(p) || personDisplayName(p),
      name: personDisplayName(p),
      person: p,
      personType,
      shiftLabel: shift?.label || shiftName || "—",
      shiftStartMin: startMin,
      status: "Absent",
      showMarkAttendance: true,
      showMarkLeave: true,
    });
  });

  const sorted = rows.sort((a, b) => {
    // Absents first, then on leave; then by earliest shift
    if (a.status !== b.status) {
      return a.status === "Absent" ? -1 : 1;
    }
    return (a.shiftStartMin ?? 9999) - (b.shiftStartMin ?? 9999);
  });

  if (limit == null || limit <= 0) return sorted;
  return sorted.slice(0, limit);
}

function mapClockInLogsToRows(logs, peopleList) {
  const byPersonId = new Map();
  const byCode = new Map();
  const byName = new Map();

  (peopleList || []).forEach((p) => {
    const id = personOrgPersonId(p);
    if (id != null) byPersonId.set(String(id), p);
    const code = String(p?.employee_code || p?.intern_code || p?.person_code || "").trim();
    if (code) byCode.set(code.toLowerCase(), p);
    const name = personDisplayName(p).toLowerCase();
    if (name && name !== "unknown") byName.set(name, p);
  });

  const latestByPerson = new Map();

  (logs || []).forEach((log) => {
    const personId = log?.organization_person_id;
    const code = String(log?.employee_code || log?.intern_code || "")
      .trim()
      .toLowerCase();
    const nameKey = String(
      log?.employee_name || log?.intern_name || log?.full_name || ""
    )
      .trim()
      .toLowerCase();

    let key;
    if (personId != null) key = `id:${personId}`;
    else if (code) key = `code:${code}`;
    else key = `name:${nameKey || Math.random()}`;

    const existing = latestByPerson.get(key);
    const timeA = log?.attendance_log_time
      ? new Date(log.attendance_log_time).getTime()
      : 0;
    const timeB = existing?.attendance_log_time
      ? new Date(existing.attendance_log_time).getTime()
      : Number.POSITIVE_INFINITY;
    // Keep earliest Clock In for the day
    if (!existing || timeA < timeB) {
      latestByPerson.set(key, log);
    }
  });

  return Array.from(latestByPerson.values())
    .map((log) => {
      const personId = log?.organization_person_id;
      const code = String(log?.employee_code || log?.intern_code || "").trim().toLowerCase();
      const nameFromLog = String(
        log?.employee_name || log?.intern_name || log?.full_name || ""
      ).trim();

      let person =
        (personId != null && byPersonId.get(String(personId))) ||
        (code && byCode.get(code)) ||
        (nameFromLog && byName.get(nameFromLog.toLowerCase())) ||
        {
          organization_person_id: personId,
          profile_image_url: log?.profile_image_url,
          first_name: nameFromLog,
        };

      const name = nameFromLog || personDisplayName(person);
      const checkIn = formatTimeValue(log?.attendance_log_time);

      // Present if they have a Clock In; Late when remarks/log hint late vs shift
      let status = "Present";
      const remark = String(log?.remarks || "").toLowerCase();
      if (remark.includes("late")) status = "Late";

      return {
        id: log?.organization_person_attendance_timelog_id || personId || name,
        name,
        person,
        checkIn,
        checkOut: "—",
        status,
        logTimeMs: log?.attendance_log_time
          ? new Date(log.attendance_log_time).getTime()
          : 0,
      };
    })
    .sort((a, b) => a.logTimeMs - b.logTimeMs);
}

function formatYmd(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Upcoming leave window: tomorrow through next 7 calendar days (inclusive). */
function getUpcomingLeaveRange(baseDate = new Date()) {
  const tomorrow = new Date(baseDate);
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const end = new Date(tomorrow);
  end.setDate(end.getDate() + 6);
  return { start: formatYmd(tomorrow), end: formatYmd(end) };
}

const OCCASION_UPCOMING_DAYS = 30;

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Upcoming occasions window: today through the next 30 calendar days (inclusive). */
function getUpcomingOccasionRange(baseDate = new Date(), days = OCCASION_UPCOMING_DAYS) {
  const start = startOfDay(baseDate);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function formatOccasionRangeLabel(start, end) {
  const opts = { day: "numeric", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-GB", opts)} – ${end.toLocaleDateString("en-GB", opts)}`;
}

function nextOccurrenceOnOrAfter(month, day, fromDate) {
  const from = startOfDay(fromDate);
  let candidate = new Date(from.getFullYear(), month, day);
  if (Number.isNaN(candidate.getTime())) return null;
  if (candidate < from) {
    candidate = new Date(from.getFullYear() + 1, month, day);
    if (Number.isNaN(candidate.getTime())) return null;
  }
  return candidate;
}

function isWithinOccasionRange(date, start, end) {
  if (!date || Number.isNaN(date.getTime())) return false;
  return date >= start && date <= end;
}

function buildLeaveListUrl(basePath, filters = {}) {
  const params = new URLSearchParams({
    page: "1",
    pageSize: "10",
    sortBy: "organization_person_leave_id",
    sortOrder: "desc",
  });
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    params.set(key, String(value));
  });
  return `${basePath}?${params.toString()}`;
}

function addDaysYmd(ymd, days) {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  d.setDate(d.getDate() + days);
  return formatYmd(d);
}

/** Calendar YYYY-MM-DD from API date / ISO (local day for datetimes). */
function toLeaveCalendarYmd(value) {
  if (value == null || value === "" || value === "-") return null;
  const s = String(value).trim();
  const plain = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (plain) return `${plain[1]}-${plain[2]}-${plain[3]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return formatYmd(d);
}

function leaveCoversYmd(row, todayYmd) {
  const start = toLeaveCalendarYmd(row?.leave_start_date);
  if (!start) return false;
  const end = toLeaveCalendarYmd(row?.leave_end_date) || start;
  return start <= todayYmd && end >= todayYmd;
}

/** True when leave overlaps any day in [rangeStartYmd, rangeEndYmd] (inclusive). */
function leaveOverlapsRange(row, rangeStartYmd, rangeEndYmd) {
  const start = toLeaveCalendarYmd(row?.leave_start_date);
  if (!start) return false;
  const end = toLeaveCalendarYmd(row?.leave_end_date) || start;
  return start <= rangeEndYmd && end >= rangeStartYmd;
}

/**
 * Broad API pull — then filter covering-today in the browser.
 * Avoids broken less_or_equal / before-after metadata operators.
 */
function buildRecentLeaveWindowApiFilters(todayYmd) {
  // Leave APIs support `between` (not `date_between`) on date columns.
  return [
    {
      field: "leave_start_date",
      operator: "between",
      value: [addDaysYmd(todayYmd, -120), addDaysYmd(todayYmd, 60)],
    },
  ];
}

function isApprovedLeave(row) {
  return String(row?.leave_status || "")
    .trim()
    .toLowerCase() === "approved";
}

function filterLeavesCoveringToday(leaveRows, todayYmd, { approvedOnly = true } = {}) {
  return (leaveRows || []).filter((row) => {
    if (approvedOnly && !isApprovedLeave(row)) return false;
    return leaveCoversYmd(row, todayYmd);
  });
}

function filterUpcomingLeaves(
  leaveRows,
  startYmd,
  endYmd,
  { approvedOnly = true } = {}
) {
  return (leaveRows || []).filter((row) => {
    if (approvedOnly && !isApprovedLeave(row)) return false;
    // Count leaves that still cover any day in the upcoming window
    // (not only leaves whose start date falls in that window).
    return leaveOverlapsRange(row, startYmd, endYmd);
  });
}

function buildOnLeaveTodayFilters(todayYmd) {
  // start ≤ today ≤ end → start in [-1y, today] AND end in [today, +1y]
  return {
    filter_leave_status: "Approved",
    filter_leave_status_operator: "equals",
    filter_leave_start_date: `${addDaysYmd(todayYmd, -365)},${todayYmd}`,
    filter_leave_start_date_operator: "between",
    filter_leave_end_date: `${todayYmd},${addDaysYmd(todayYmd, 365)}`,
    filter_leave_end_date_operator: "between",
  };
}

function buildUpcomingLeaveFilters(startYmd, endYmd) {
  // Overlap window: leave_start ≤ end AND leave_end ≥ start
  return {
    filter_leave_status: "Approved",
    filter_leave_status_operator: "equals",
    filter_leave_start_date: `${addDaysYmd(startYmd, -365)},${endYmd}`,
    filter_leave_start_date_operator: "between",
    filter_leave_end_date: `${startYmd},${addDaysYmd(endYmd, 365)}`,
    filter_leave_end_date_operator: "between",
  };
}

function buildPendingLeaveFilters() {
  return {
    filter_leave_status: "Pending",
    filter_leave_status_operator: "equals",
  };
}

function countUniqueLeavePeople(leaveRows) {
  const ids = new Set();
  (leaveRows || []).forEach((row) => {
    const id = row?.organization_person_id;
    if (id != null) ids.add(String(id));
    else {
      const code = String(row?.employee_code || row?.intern_code || "")
        .trim()
        .toLowerCase();
      if (code) ids.add(`code:${code}`);
    }
  });
  return ids.size;
}

function personCode(p) {
  return String(
    p?.Employee_Code ||
      p?.employee_code ||
      p?.Intern_Code ||
      p?.intern_code ||
      p?.person_code ||
      ""
  )
    .trim()
    .toLowerCase();
}

function employmentTypeNameOf(p) {
  if (typeof p?.Employment_Type === "string" && p.Employment_Type.trim()) {
    return p.Employment_Type.trim();
  }
  if (typeof p?.employment_type_name === "string" && p.employment_type_name.trim()) {
    return p.employment_type_name.trim();
  }
  if (typeof p?.employment_type === "string" && p.employment_type.trim()) {
    return p.employment_type.trim();
  }
  return "";
}

function isFreelanceEmploymentType(name) {
  return String(name || "")
    .toLowerCase()
    .includes("freelance");
}

function isEmployeeEmploymentType(name) {
  return String(name || "")
    .toLowerCase()
    .includes("employee");
}

/** Birthday / anniversary / new-joining counts for a people list (next 30 days). */
function countOccasionsForPeople(
  people,
  parseBirthDate,
  parseJoiningDate,
  currentDate,
  days = OCCASION_UPCOMING_DAYS
) {
  const { start, end } = getUpcomingOccasionRange(currentDate, days);
  let newJoining = 0;
  let birthday = 0;
  let anniversary = 0;
  const seenBirthday = new Set();
  const seenAnniversary = new Set();
  const seenJoining = new Set();

  const personKey = (person, index) => {
    const id = personOrgPersonId(person);
    if (id != null && String(id).trim() !== "" && String(id) !== "-") {
      return String(id);
    }
    const code = personCode(person);
    return code ? `code:${code}` : `row:${index}`;
  };

  (people || []).forEach((person, index) => {
    const key = personKey(person, index);
    if (person?.Date_of_Joining) {
      const joiningDateObj = parseJoiningDate(person.Date_of_Joining);
      if (joiningDateObj) {
        const nextJoinOccurrence = nextOccurrenceOnOrAfter(
          joiningDateObj.getMonth(),
          joiningDateObj.getDate(),
          start
        );
        if (isWithinOccasionRange(nextJoinOccurrence, start, end)) {
          const joinYear = joiningDateObj.getFullYear();
          const occurrenceYear = nextJoinOccurrence.getFullYear();
          if (joinYear === occurrenceYear) {
            if (!seenJoining.has(key)) {
              seenJoining.add(key);
              newJoining += 1;
            }
          } else if (joinYear < occurrenceYear) {
            if (!seenAnniversary.has(key)) {
              seenAnniversary.add(key);
              anniversary += 1;
            }
          }
        }
      }
    }
    if (person?.Date_of_Birth) {
      const birthDateObj = parseBirthDate(person.Date_of_Birth);
      if (birthDateObj) {
        const nextBirthday = nextOccurrenceOnOrAfter(
          birthDateObj.getMonth(),
          birthDateObj.getDate(),
          start
        );
        if (isWithinOccasionRange(nextBirthday, start, end)) {
          if (!seenBirthday.has(key)) {
            seenBirthday.add(key);
            birthday += 1;
          }
        }
      }
    }
  });

  return { newJoining, birthday, anniversary };
}

/** Same visibility rule as Intern list page: hide Exited (converted still shown). */
function isInternVisibleOnInternList(person) {
  if (person?.is_converted_to_employee) return true;
  const status = String(
    person?.Internship_Status ||
      person?.internship_status ||
      person?.Status ||
      person?.status ||
      ""
  ).toLowerCase();
  return !status.includes("exit");
}

function splitEmployeeFreelanceLeaveCounts(
  leaveRows,
  freelancersList,
  employeesList = [],
  { uniquePeople = true } = {}
) {
  const freelancerIds = new Set();
  const freelancerCodes = new Set();

  const rememberPerson = (p) => {
    const id = personOrgPersonId(p);
    if (id != null && String(id).trim() !== "") freelancerIds.add(String(id));
    const code = personCode(p);
    if (code) freelancerCodes.add(code);
  };

  (freelancersList || []).forEach(rememberPerson);
  (employeesList || []).forEach((p) => {
    if (isFreelanceEmploymentType(employmentTypeNameOf(p))) rememberPerson(p);
  });

  const empIds = new Set();
  const freeIds = new Set();
  let empRows = 0;
  let freeRows = 0;

  (leaveRows || []).forEach((row, index) => {
    const id = row?.organization_person_id;
    const code = personCode(row);
    const fromType = isFreelanceEmploymentType(employmentTypeNameOf(row));
    const isFree =
      fromType ||
      (id != null && freelancerIds.has(String(id))) ||
      (code && freelancerCodes.has(code));
    const key =
      id != null && String(id).trim() !== "" && String(id) !== "-"
        ? String(id)
        : code
          ? `code:${code}`
          : `row:${index}`;
    if (isFree) {
      freeRows += 1;
      freeIds.add(key);
    } else {
      empRows += 1;
      empIds.add(key);
    }
  });

  if (uniquePeople) {
    return { employees: empIds.size, freelancers: freeIds.size };
  }
  return { employees: empRows, freelancers: freeRows };
}

async function fetchLeaveFilteredCount(orgId, filteredPath, filters) {
  const response = await fetch(
    `${MAIN_URL}/api/organizations/${orgId}/${filteredPath}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        filters,
        page: 1,
        pageSize: 1,
        sort: {
          field: "organization_person_leave_id",
          direction: "desc",
        },
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = await response.json();
  const total =
    result?.pagination?.total ??
    result?.pagination?.Total ??
    result?.total ??
    (Array.isArray(result?.data) ? result.data.length : 0);
  return Number(total) || 0;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const org = React.useMemo(
    () => resolveOrganizationFromUserData(userData),
    [userData]
  );
  const [employees, setEmployees] = React.useState([]);
  const [interns, setInterns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const { Permission, isPermissionLoaded, setPermission } =
  usePermissionDataStore();

  const [onLeaveTodayApiCount, setOnLeaveTodayApiCount] = React.useState(null);

  const [apiUrl, setApiUrl] = React.useState(null);
  const [internsApiUrl, setInternsApiUrl] = React.useState(null);

  const [freelancers, setFreelancers] = React.useState([]);
  const [freelancersApiUrl, setFreelancersApiUrl] = React.useState(null);
  const [todayClockInLogs, setTodayClockInLogs] = React.useState({
    employees: [],
    interns: [],
  });
  const [workShifts, setWorkShifts] = React.useState([]);
  const [todayLeaveRows, setTodayLeaveRows] = React.useState({
    employees: [],
    interns: [],
  });
  const [todayAttLoading, setTodayAttLoading] = React.useState(false);
  // Raw leave windows — counts derived with freelancers via useMemo (avoids refetch races).
  const [leaveOverviewRaw, setLeaveOverviewRaw] = React.useState({
    empWindow: [],
    intWindow: [],
    empPendingRows: [],
    intPending: 0,
    todayYmd: "",
    upcomingStart: "",
    upcomingEnd: "",
  });
  const [leaveOverviewLoading, setLeaveOverviewLoading] = React.useState(false);

  console.log("permission coming out is : ", Permission);

  React.useEffect(() => {
    if (org?.organization_id) {
      setApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/filter-employee`
      );
      setInternsApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/filter/intern`
      );
      setFreelancersApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/freelance-employees`
      );
    }
  }, [org?.organization_id]);


  console.log("dsata is ojoj", internsApiUrl);
  

  const currentDate = new Date();
  const parseBirthDate = React.useCallback((dateString) => {
    if (!dateString || typeof dateString !== "string") return null;

    // Handles ISO-like formats such as "2005-03-11" and "2005-03-11T00:00:00.000Z"
    const isoLikePattern = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
    if (isoLikePattern.test(dateString)) {
      const parsed = new Date(dateString);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Handles "dd-MMM-yyyy" format such as "09-Sep-2025"
    const monthNames = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];

    const birthParts = dateString.split("-");
    if (birthParts.length === 3) {
      const day = Number(birthParts[0]);
      const monthIndex = monthNames.indexOf(
        String(birthParts[1]).slice(0, 3).toLowerCase()
      );
      const year = Number(birthParts[2]);

      if (!Number.isNaN(day) && monthIndex !== -1 && !Number.isNaN(year)) {
        return new Date(year, monthIndex, day);
      }
    }

    return null;
  }, []);
  const parseJoiningDate = React.useCallback((dateString) => {
    if (dateString == null || dateString === "") return null;
    if (dateString instanceof Date) {
      return Number.isNaN(dateString.getTime()) ? null : dateString;
    }
    if (typeof dateString !== "string") {
      const asDate = new Date(dateString);
      return Number.isNaN(asDate.getTime()) ? null : asDate;
    }

    // Handles ISO-like formats such as "2026-03-09" and "2026-03-09T00:00:00.000Z"
    const isoLikePattern = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
    if (isoLikePattern.test(dateString)) {
      const parsed = new Date(dateString);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Handles "dd-MMM-yyyy" format such as "09-Sep-2025"
    const monthNames = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];

    const joiningParts = dateString.split("-");
    if (joiningParts.length === 3) {
      const day = Number(joiningParts[0]);
      const monthIndex = monthNames.indexOf(
        String(joiningParts[1]).slice(0, 3).toLowerCase()
      );
      const year = Number(joiningParts[2]);

      if (!Number.isNaN(day) && monthIndex !== -1 && !Number.isNaN(year)) {
        return new Date(year, monthIndex, day);
      }
    }

    const fallback = new Date(dateString);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }, []);

  // Employee-only (exclude Freelance) — used by personalize widgets before active lists exist.
  const calculateWidgetValues = React.useMemo(() => {
    const employeePeople = (employees || []).filter(
      (e) => !isFreelanceEmploymentType(employmentTypeNameOf(e))
    );
    return countOccasionsForPeople(
      employeePeople,
      parseBirthDate,
      parseJoiningDate,
      currentDate
    );
  }, [employees, currentDate, parseBirthDate, parseJoiningDate]);

  const calculateInternWidgetValues = React.useMemo(
    () =>
      countOccasionsForPeople(
        interns,
        parseBirthDate,
        parseJoiningDate,
        currentDate
      ),
    [interns, currentDate, parseBirthDate, parseJoiningDate]
  );

  const calculateFreelancerWidgetValues = React.useMemo(
    () =>
      countOccasionsForPeople(
        freelancers,
        parseBirthDate,
        parseJoiningDate,
        currentDate
      ),
    [freelancers, currentDate, parseBirthDate, parseJoiningDate]
  );

  const attendanceStats = React.useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;

    const normalizeBool = (val) => {
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (
          [
            "true",
            "1",
            "yes",
            "y",
            "present",
            "checked-in",
            "clocked-in",
            "in",
          ].includes(s)
        )
          return true;
        if (
          ["false", "0", "no", "n", "absent", "not-present", "out"].includes(s)
        )
          return false;
      }
      return null;
    };

    const isNonEmptyTime = (val) => {
      if (val == null) return false;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (!s) return false;
        if (["null", "undefined", "na", "n/a"].includes(s)) return false;
        if (
          ["00:00", "00:00:00", "0", "1970-01-01", "invalid date"].includes(s)
        )
          return false;
        return true;
      }
      if (val instanceof Date && !isNaN(val.getTime())) return true;
      return false;
    };

    employees.forEach((emp) => {
      if (!isActiveWorkforcePerson(emp, localYmd(currentDate))) return;

      const today =
        emp?.attendance_today ||
        emp?.Attendance?.[0] ||
        emp?.attendance?.[0] ||
        null;

      const clockInCandidates = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        emp?.clock_in,
        emp?.ClockIn,
        emp?.clockIn,
        emp?.clockin,
      ];
      const clockOutCandidates = [
        today?.clock_out,
        today?.ClockOut,
        today?.clockOut,
        today?.clockout,
        emp?.clock_out,
        emp?.ClockOut,
        emp?.clockOut,
        emp?.clockout,
      ];

      let clockInFlag = null;
      for (const c of clockInCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockInFlag = n;
          break;
        }
      }

      let clockOutFlag = null;
      for (const c of clockOutCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockOutFlag = n;
          break;
        }
      }

      const hasCheckInTimestamp = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        today?.check_in,
        today?.CheckIn,
        today?.checkIn,
        today?.time_in,
        today?.TimeIn,
        today?.in_time,
        today?.first_check_in,
        today?.firstCheckIn,
      ].some(isNonEmptyTime);

      let counted = false;

      if (clockInFlag === false || clockOutFlag === true) {
        present += 1;
        counted = true;
      } else if (clockInFlag === true && !hasCheckInTimestamp) {
        absent += 1;
        counted = true;
      } else if (hasCheckInTimestamp) {
        present += 1;
        counted = true;
      }

      if (!counted) {
        const rawStatus =
          today?.status ||
          today?.Status ||
          emp?.AttendanceStatus ||
          emp?.today_status ||
          emp?.status_today ||
          "";
        const status =
          typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
        if (
          status.includes("present") ||
          status === "in" ||
          status === "checked-in"
        )
          present += 1;
        else if (
          status.includes("absent") ||
          status === "out" ||
          status === "checked-out"
        )
          absent += 1;
      }

      if (
        normalizeBool(today?.late) === true ||
        normalizeBool(emp?.late_today) === true
      ) {
        late += 1;
      } else {
        const statusLate = String(
          today?.status || today?.Status || ""
        ).toLowerCase();
        if (statusLate.includes("late")) late += 1;
      }
    });

    return { present, absent, late };
  }, [employees, currentDate]);

  // React.useEffect(() => {
  //   const orgId = org?.organization_id || 5;
  //   if (!orgId) return;

  //   const leavesUrl = `${MAIN_URL}/api/organizations/${orgId}/employee-leaves-all`;
  //   const controller = new AbortController();

  //   const fetchLeaves = async () => {
  //     try {
  //       const response = await fetch(leavesUrl, {
  //         method: "GET",
  //         headers: {
  //           Accept: "application/json",
  //         },
  //         signal: controller.signal,
  //       });
  //       if (!response.ok)
  //         throw new Error(`HTTP error! status: ${response.status}`);

  //       const json = await response.json();
  //       let leaves = Array.isArray(json)
  //         ? json
  //         : json.data || json.leaves || json.results || [];

  //       if (
  //         Array.isArray(leaves) &&
  //         leaves.length > 0 &&
  //         Array.isArray(leaves[0])
  //       ) {
  //         leaves = leaves[0];
  //       }

  //       const y = currentDate.getFullYear();
  //       const m = currentDate.getMonth();
  //       const d = currentDate.getDate();
  //       const isSameDay = (dateObj) =>
  //         dateObj &&
  //         dateObj.getFullYear() === y &&
  //         dateObj.getMonth() === m &&
  //         dateObj.getDate() === d;

  //       const count = (leaves || []).reduce((acc, item) => {
  //         const startStr =
  //           item?.leave_start_date ||
  //           item?.start_date ||
  //           item?.from_date ||
  //           item?.fromDate;
  //         if (!startStr) return acc;
  //         const start = new Date(startStr);
  //         return isSameDay(start) ? acc + 1 : acc;
  //       }, 0);

  //       setOnLeaveTodayApiCount(count);
  //     } catch (e) {
  //       setOnLeaveTodayApiCount(0);
  //     }
  //   };

  //   fetchLeaves();
  //   return () => controller.abort();
  // }, [org?.organization_id, currentDate]);

  const leavesStats = React.useMemo(() => {
    let onLeaveToday = 0;
    let upcomingLeaves = 0;
    let pending = 0;

    const now = currentDate;
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    employees.forEach((emp) => {
      const todayLeave =
        emp.today_leave ||
        emp.LeaveToday ||
        emp.leave_today ||
        emp.is_on_leave_today ||
        false;
      if (todayLeave) onLeaveToday += 1;

      const pendingReq = Number(
        emp.pending_leave_requests || emp.pendingLeaves || 0
      );
      if (!Number.isNaN(pendingReq)) pending += pendingReq;

      const upcomingArr =
        emp.upcoming_leaves || emp.UpcomingLeaves || emp.upcomingLeaves || [];

      if (Array.isArray(upcomingArr)) {
        upcomingLeaves += upcomingArr.filter((l) => {
          const startStr = l?.start_date || l?.startDate;
          if (!startStr) return false;
          const start = new Date(startStr);
          return start >= now && start <= in7Days;
        }).length;
      }
    });

    const finalOnLeaveToday =
      typeof onLeaveTodayApiCount === "number"
        ? onLeaveTodayApiCount
        : onLeaveToday;

    return { onLeaveToday: finalOnLeaveToday, upcomingLeaves, pending };
  }, [employees, currentDate, onLeaveTodayApiCount]);

  const fetchEmployees = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!apiUrl) {
        setEmployees([]);
        return;
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let employeesData = Array.isArray(data)
        ? data
        : data.employees || data.data || [];

      if (
        Array.isArray(employeesData) &&
        employeesData.length > 0 &&
        Array.isArray(employeesData[0])
      ) {
        employeesData = employeesData[0];
      }

      setEmployees(employeesData);
    } catch (err) {
      let errorMessage = "Failed to fetch employee data";
      if (
        err?.name === "TypeError" &&
        String(err?.message || "").includes("Failed to fetch")
      ) {
        errorMessage =
          "Network error: Unable to connect to the API. This might be due to CORS restrictions or network connectivity issues.";
      } else if (String(err?.message || "").includes("HTTP error")) {
        errorMessage = `API Error: ${err.message}`;
      } else if (err?.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch freelancers function
  const fetchFreelancers = React.useCallback(async () => {
    try {
      if (!freelancersApiUrl) {
        setFreelancers([]);
        return;
      }

      const response = await fetch(freelancersApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let freelancersData = Array.isArray(data)
        ? data
        : data.freelancers || data.data || [];

      if (
        Array.isArray(freelancersData) &&
        freelancersData.length > 0 &&
        Array.isArray(freelancersData[0])
      ) {
        freelancersData = freelancersData[0];
      }

      setFreelancers(freelancersData);
    } catch (err) {
      console.error("Failed to fetch freelancer data:", err);
      setFreelancers([]);
    }
  }, [freelancersApiUrl]);

  React.useEffect(() => {
    fetchFreelancers();
  }, [fetchFreelancers]);

  // NEW: Fetch interns function
  const fetchInterns = React.useCallback(async () => {
    try {
      if (!internsApiUrl) {
        setInterns([]);
        return;
      }

      const response = await fetch(internsApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let internsData = Array.isArray(data)
        ? data
        : data.interns || data.data || [];

      if (
        Array.isArray(internsData) &&
        internsData.length > 0 &&
        Array.isArray(internsData[0])
      ) {
        internsData = internsData[0];
      }

      setInterns(internsData);
    } catch (err) {
      console.error("Failed to fetch intern data:", err);
      setInterns([]);
    }
  }, [internsApiUrl]);



  React.useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  React.useEffect(() => {
    fetchInterns();
  }, [fetchInterns]);

  React.useEffect(() => {
    const orgId = org?.organization_id;
    if (!orgId) return;

    let cancelled = false;
    const loadTodayAttendanceContext = async () => {
      setTodayAttLoading(true);
      const todayYmd = formatYmd(new Date());

      try {
        const [employeeLogs, internLogs, shifts, empLeavesRaw, intLeavesRaw] =
          await Promise.all([
            fetchTodayClockInLogs(orgId, "attendance-time-logs-filtered").catch(
              () => []
            ),
            fetchTodayClockInLogs(orgId, "intern-time-logs-filtered").catch(
              () => []
            ),
            fetchWorkShifts(orgId).catch(() => []),
            fetchLeaveRowsForDashboard(orgId, "employee-leave/filters", todayYmd),
            fetchLeaveRowsForDashboard(orgId, "intern-leaves/filters", todayYmd),
          ]);
        if (!cancelled) {
          setTodayClockInLogs({
            employees: Array.isArray(employeeLogs) ? employeeLogs : [],
            interns: Array.isArray(internLogs) ? internLogs : [],
          });
          setWorkShifts(Array.isArray(shifts) ? shifts : []);
          setTodayLeaveRows({
            employees: filterLeavesCoveringToday(empLeavesRaw, todayYmd),
            interns: filterLeavesCoveringToday(intLeavesRaw, todayYmd),
          });
        }
      } catch (e) {
        if (!cancelled) {
          setTodayClockInLogs({ employees: [], interns: [] });
          setWorkShifts([]);
          setTodayLeaveRows({ employees: [], interns: [] });
        }
      } finally {
        if (!cancelled) setTodayAttLoading(false);
      }
    };

    loadTodayAttendanceContext();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  React.useEffect(() => {
    const orgId = org?.organization_id;
    if (!orgId) return;

    let cancelled = false;
    const loadLeaveOverview = async () => {
      setLeaveOverviewLoading(true);
      const todayYmd = formatYmd(new Date());
      const { start, end } = getUpcomingLeaveRange(new Date());
      const pendingFilters = [
        { field: "leave_status", operator: "equals", value: "Pending" },
      ];

      try {
        const [empLeaveWindow, intLeaveWindow, empPendingRows, intPending] =
          await Promise.all([
            fetchLeaveRowsForDashboard(
              orgId,
              "employee-leave/filters",
              todayYmd
            ),
            fetchLeaveRowsForDashboard(
              orgId,
              "intern-leaves/filters",
              todayYmd
            ),
            fetchLeaveRows(
              orgId,
              "employee-leave/filters",
              pendingFilters,
              500
            ).catch(() => []),
            fetchLeaveFilteredCount(
              orgId,
              "intern-leaves/filters",
              pendingFilters
            ).catch(() => 0),
          ]);

        if (cancelled) return;

        const empOnLeaveRows = filterLeavesCoveringToday(
          empLeaveWindow,
          todayYmd
        );
        const intOnLeaveRows = filterLeavesCoveringToday(
          intLeaveWindow,
          todayYmd
        );

        setLeaveOverviewRaw({
          empWindow: Array.isArray(empLeaveWindow) ? empLeaveWindow : [],
          intWindow: Array.isArray(intLeaveWindow) ? intLeaveWindow : [],
          empPendingRows: Array.isArray(empPendingRows) ? empPendingRows : [],
          intPending: Number(intPending) || 0,
          todayYmd,
          upcomingStart: start,
          upcomingEnd: end,
        });
        setTodayLeaveRows({
          employees: empOnLeaveRows,
          interns: intOnLeaveRows,
        });
      } catch {
        if (cancelled) return;
        setLeaveOverviewRaw({
          empWindow: [],
          intWindow: [],
          empPendingRows: [],
          intPending: 0,
          todayYmd: formatYmd(new Date()),
          upcomingStart: "",
          upcomingEnd: "",
        });
      } finally {
        // Always clear loading for this run so UI never sticks on "—"
        // after a cancelled re-fetch from unstable deps.
        if (!cancelled) setLeaveOverviewLoading(false);
      }
    };

    loadLeaveOverview();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  const leaveOverviewCounts = React.useMemo(() => {
    const todayYmd = leaveOverviewRaw.todayYmd || formatYmd(new Date());
    const start =
      leaveOverviewRaw.upcomingStart ||
      getUpcomingLeaveRange(new Date()).start;
    const end =
      leaveOverviewRaw.upcomingEnd || getUpcomingLeaveRange(new Date()).end;

    const empOnLeaveRows = filterLeavesCoveringToday(
      leaveOverviewRaw.empWindow,
      todayYmd
    );
    const intOnLeaveRows = filterLeavesCoveringToday(
      leaveOverviewRaw.intWindow,
      todayYmd
    );
    const empUpcomingRows = filterUpcomingLeaves(
      leaveOverviewRaw.empWindow,
      start,
      end
    );
    const intUpcomingRows = filterUpcomingLeaves(
      leaveOverviewRaw.intWindow,
      start,
      end
    );

    const { employees: empOnLeave, freelancers: freeOnLeave } =
      splitEmployeeFreelanceLeaveCounts(
        empOnLeaveRows,
        freelancers,
        employees
      );
    const { employees: empUpcoming, freelancers: freeUpcoming } =
      splitEmployeeFreelanceLeaveCounts(
        empUpcomingRows,
        freelancers,
        employees
      );
    const { employees: empPending, freelancers: freePending } =
      splitEmployeeFreelanceLeaveCounts(
        leaveOverviewRaw.empPendingRows,
        freelancers,
        employees,
        { uniquePeople: false }
      );

    return {
      employees: {
        onLeave: empOnLeave,
        upcoming: empUpcoming,
        pending: empPending,
      },
      interns: {
        onLeave: countUniqueLeavePeople(intOnLeaveRows),
        upcoming: countUniqueLeavePeople(intUpcomingRows),
        pending: leaveOverviewRaw.intPending,
      },
      freelancers: {
        onLeave: freeOnLeave,
        upcoming: freeUpcoming,
        pending: freePending,
      },
    };
  }, [leaveOverviewRaw, freelancers, employees]);

  const genderCounts = employees.reduce((acc, emp) => {
    const gender = emp.gender || emp.Gender || emp.sex || emp.Sex || "Unknown";
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderData = [
    {
      id: 0,
      value:
        genderCounts["Male"] || genderCounts["male"] || genderCounts["M"] || 0,
      label: "Male",
      color: "var(--ems-chart-1)",
    },
    {
      id: 1,
      value:
        genderCounts["Female"] ||
        genderCounts["female"] ||
        genderCounts["F"] ||
        0,
      label: "Female",
      color: "var(--ems-chart-2)",
    },
    {
      id: 2,
      value: Object.keys(genderCounts).reduce((otherCount, key) => {
        const normalizedKey = key.toLowerCase();
        if (
          normalizedKey !== "male" &&
          normalizedKey !== "female" &&
          normalizedKey !== "m" &&
          normalizedKey !== "f"
        ) {
          return otherCount + genderCounts[key];
        }
        return otherCount;
      }, 0),
      label: "Other",
      color: "var(--ems-chart-3)",
    },
  ];

  const getDepartmentShortName = (fullName, preferredShort) => {
    const full = String(fullName || "").trim();
    const preferred = String(preferredShort || "").trim();
    if ((!full || full === "Unknown") && !preferred) return "UNK";

    const normalize = (value) =>
      String(value || "")
        .toLowerCase()
        // Treat any ampersand-like char as a word break
        .replace(/[&＋＆]/g, " ")
        .replace(/\s*(department|dept|division|div)\s*$/i, "")
        .replace(/[/\\#,+()$~%.'":*?<>{}-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const toInitials = (value) => {
      const words = normalize(value)
        .split(" ")
        .filter(
          (w) => w && !["and", "of", "the", "for", "to", "a", "an"].includes(w)
        );
      if (words.length >= 2) {
        return words
          .slice(0, 3)
          .map((w) => w[0])
          .join("")
          .toUpperCase();
      }
      return (words[0] || normalize(value) || "UNK").slice(0, 4).toUpperCase();
    };

    const isCompactCode = (value) => {
      const v = String(value || "").trim().toUpperCase();
      // Compact axis codes only: DM, MP, WD, LEAD, CD, BSP (no spaces)
      return Boolean(v) && v.length <= 4 && !/[\s&]/.test(v);
    };

    const abbreviationMap = {
      "human resources": "HR",
      hr: "HR",
      finance: "FIN",
      fin: "FIN",
      "information technology": "IT",
      it: "IT",
      tech: "IT",
      technology: "IT",
      marketing: "MKT",
      mkt: "MKT",
      "digital marketing": "DM",
      dm: "DM",
      "media production": "MP",
      mp: "MP",
      "web designing": "WD",
      "web design": "WD",
      wd: "WD",
      leadership: "LEAD",
      lead: "LEAD",
      "creative design": "CD",
      "creative des": "CD",
      "creative and design": "CD",
      cd: "CD",
      "brand strategy plan": "BSP",
      "brand stgy plan": "BSP",
      "brand strategy and plan": "BSP",
      "brand strategy planning": "BSP",
      bsp: "BSP",
      sales: "SAL",
      operations: "OPS",
      ops: "OPS",
      "research and development": "RD",
      "r d": "RD",
      research: "RD",
      development: "RD",
      "customer service": "CS",
      cs: "CS",
      support: "CS",
      legal: "LGL",
      administration: "ADM",
      admin: "ADM",
    };

    const cleanFull = normalize(full);
    const cleanPreferred = normalize(preferred);

    if (abbreviationMap[cleanPreferred]) return abbreviationMap[cleanPreferred];
    if (abbreviationMap[cleanFull]) return abbreviationMap[cleanFull];

    // Use DB short name only when already a compact code (DM, MP, WD, LEAD)
    if (isCompactCode(preferred)) return preferred.toUpperCase();

    // Long DB "short" names like "CREATIVE & DES" / "BRAND STGY & PLAN"
    // must become initials — never show on the axis as-is
    if (preferred && !isCompactCode(preferred)) {
      return toInitials(preferred);
    }

    return toInitials(full || preferred);
  };

  const departmentCounts = employees.reduce((acc, emp) => {
    let fullName = "Unknown";
    let preferredShort = "";

    if (typeof emp.Department === "string" && emp.Department.trim()) {
      fullName = emp.Department.trim();
      preferredShort =
        emp.Department_Short_Name ||
        emp.department_short_name ||
        "";
    } else if (
      emp.Department &&
      Array.isArray(emp.Department) &&
      emp.Department.length > 0
    ) {
      const deptObj = emp.Department[0];
      fullName =
        deptObj?.department?.department_name ||
        deptObj?.department?.name ||
        deptObj?.department?.Department ||
        deptObj?.name ||
        deptObj?.Department ||
        "Unknown";
      preferredShort =
        deptObj?.department?.department_short_name ||
        deptObj?.department_short_name ||
        "";
    } else if (emp.department) {
      fullName =
        emp.department.department_name ||
        emp.department.name ||
        emp.department.Department ||
        "Unknown";
      preferredShort = emp.department.department_short_name || "";
    } else if (emp.department_name) {
      fullName = emp.department_name;
      preferredShort = emp.department_short_name || "";
    }

    if (!fullName || fullName === "") {
      fullName = "Unknown";
    }

    if (!acc[fullName]) {
      acc[fullName] = {
        fullName,
        name: getDepartmentShortName(fullName, preferredShort),
        value: 0,
      };
    }
    acc[fullName].value += 1;
    return acc;
  }, {});

  // Ensure short labels stay unique across departments
  const usedShortNames = new Set();
  Object.values(departmentCounts).forEach((item) => {
    let short = item.name;
    if (usedShortNames.has(short)) {
      let i = 2;
      while (usedShortNames.has(`${short}${i}`)) i += 1;
      short = `${short}${i}`;
    }
    usedShortNames.add(short);
    item.name = short;
  });

  const departmentData = Object.values(departmentCounts).map(
    ({ name, fullName, value }) => ({ name, fullName, value })
  );

  const employmentTypeCounts = employees.reduce((acc, emp) => {
    let employmentTypeName = "Unknown";

    // filter-employee API returns Employment_Type as a string
    if (typeof emp.Employment_Type === "string" && emp.Employment_Type.trim()) {
      employmentTypeName = emp.Employment_Type.trim();
    } else if (typeof emp.employment_type === "string" && emp.employment_type.trim()) {
      employmentTypeName = emp.employment_type.trim();
    } else if (
      emp.EmploymentType &&
      Array.isArray(emp.EmploymentType) &&
      emp.EmploymentType.length > 0
    ) {
      const empTypeObj = emp.EmploymentType[0];
      employmentTypeName =
        empTypeObj?.employment_type?.name ||
        empTypeObj?.employment_type?.EmploymentType ||
        empTypeObj?.employment_type?.employment_type_name ||
        empTypeObj?.name ||
        empTypeObj?.EmploymentType ||
        empTypeObj?.employment_type_name;
    } else if (emp.employment_type && typeof emp.employment_type === "object") {
      employmentTypeName =
        emp.employment_type.name ||
        emp.employment_type.EmploymentType ||
        emp.employment_type.employment_type_name ||
        emp.employment_type.Employment_Type;
    } else if (emp.employemnettype && typeof emp.employemnettype === "object") {
      employmentTypeName =
        emp.employemnettype.employment_type_name ||
        emp.employemnettype.name;
    } else if (emp.employment_type_name) {
      employmentTypeName = emp.employment_type_name;
    } else if (typeof emp.EmploymentType === "string" && emp.EmploymentType.trim()) {
      employmentTypeName = emp.EmploymentType.trim();
    }

    if (
      !employmentTypeName ||
      employmentTypeName === "" ||
      employmentTypeName === "-" ||
      employmentTypeName === "null" ||
      employmentTypeName === "undefined"
    ) {
      employmentTypeName = "Unknown";
    }

    if (employmentTypeName && employmentTypeName !== "Unknown") {
      employmentTypeName = String(employmentTypeName)
        .replace(/\btype\b/gi, "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    if (!employmentTypeName) {
      employmentTypeName = "Unknown";
    }

    acc[employmentTypeName] = (acc[employmentTypeName] || 0) + 1;
    return acc;
  }, {});

  const employmentTypeData = Object.entries(employmentTypeCounts).map(
    ([name, value]) => ({ name, value })
  );

  const getShortDesignationName = (designation) => {
    const abbreviationMap = {
      "chief executive officer": "CEO",
      "chief technology officer": "CTO",
      "chief financial officer": "CFO",
      "chief operating officer": "COO",
      "chief marketing officer": "CMO",
      "chief human resources officer": "CHRO",
      "general manager": "GM",
      "project manager": "PM",
      "product manager": "PM",
      "program manager": "PGM",
      "operations manager": "OM",
      "marketing manager": "MM",
      "sales manager": "SM",
      "finance manager": "FinM",
      "hr manager": "HRM",
      "human resources manager": "HRM",
      "security manager": "SM",
      "quality manager": "QM",
      "business development manager": "BDM",
      "software engineer": "SWE",
      "senior software engineer": "SrSE",
      "junior software engineer": "JrSE",
      "software developer": "Dev",
      "senior software developer": "SrDev",
      "junior software developer": "JrDev",
      "full stack developer": "FullStack",
      "frontend developer": "Frontend",
      "backend developer": "Backend",
      "mobile developer": "MobileDev",
      "web developer": "WebDev",
      "devops engineer": "DevOps",
      "system administrator": "SysAdmin",
      "database administrator": "DBA",
      "network administrator": "NetAdmin",
      "quality assurance": "QA",
      "quality assurance engineer": "QA Eng",
      "test engineer": "Test Eng",
      "automation engineer": "Auto Eng",
      "business analyst": "BA",
      "senior business analyst": "Sr BA",
      "data analyst": "DA",
      "senior data analyst": "Sr DA",
      "data scientist": "DS",
      "business intelligence analyst": "BI Analyst",
      "financial analyst": "Fin Analyst",
      "market research analyst": "MR Analyst",
      "sales executive": "Sales Exec",
      "senior sales executive": "Sr Sales",
      "sales representative": "Sales Rep",
      "account manager": "Acct Mgr",
      "account executive": "Acct Exec",
      "marketing executive": "Mkt Exec",
      "digital marketing specialist": "Digital Mkt",
      "content marketing specialist": "Content Mkt",
      "social media manager": "SM Mgr",
      "human resource executive": "HRE",
      "hr executive": "HRE",
      "human resource specialist": "HR Spec",
      "hr specialist": "HR Spec",
      recruiter: "Recruiter",
      "talent acquisition specialist": "TA Spec",
      "administrative assistant": "AdminAsst",
      "executive assistant": "ExecAsst",
      "office manager": "Office Mgr",
      accountant: "Accountant",
      "senior accountant": "Sr Accountant",
      "junior accountant": "Jr Accountant",
      "accounts payable": "AP",
      "accounts receivable": "AR",
      "financial controller": "Controller",
      "finance executive": "FIE",
      "finance head": "FIH",
      "customer service representative": "CSR",
      "customer support": "Support",
      "customer success manager": "CS Mgr",
      "technical support": "Tech Support",
      "help desk": "Help Desk",
      "ui designer": "UI Designer",
      "ux designer": "UX Designer",
      "ui/ux designer": "UI/UX",
      "graphic designer": "GFX Designer",
      "web designer": "Web Designer",
      "product designer": "Product Des",
      "operations executive": "Ops Exec",
      "logistics coordinator": "Logistics",
      "supply chain manager": "SC Mgr",
      "warehouse manager": "WH Mgr",
      "production manager": "Prod Mgr",
      consultant: "Consultant",
      "senior consultant": "Sr Consultant",
      "strategy consultant": "Strategy Con",
      "business consultant": "Biz Consultant",
      "legal counsel": "Legal",
      "compliance officer": "Compliance",
      "legal advisor": "Legal Advisor",
    };

    let cleanDesignation = designation.toLowerCase().trim();

    const suffixesToRemove = [
      " specialist",
      " executive",
      " representative",
      " coordinator",
      " associate",
    ];
    suffixesToRemove.forEach((suffix) => {
      if (
        cleanDesignation.endsWith(suffix) &&
        !abbreviationMap[cleanDesignation]
      ) {
        const withoutSuffix = cleanDesignation.replace(suffix, "").trim();
        if (abbreviationMap[withoutSuffix]) {
          cleanDesignation = withoutSuffix;
        }
      }
    });

    if (abbreviationMap[cleanDesignation]) {
      return abbreviationMap[cleanDesignation];
    }

    for (const [fullName, abbrev] of Object.entries(abbreviationMap)) {
      if (
        fullName.includes(cleanDesignation) ||
        cleanDesignation.includes(fullName.split(" ")[0])
      ) {
        return abbrev;
      }
    }

    const formatted = designation
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return formatted.length > 12
      ? formatted.substring(0, 12) + "..."
      : formatted;
  };

  const designationCounts = employees.reduce((acc, emp) => {
    const designationName = emp.Designation || "Unknown";
    const shortName = getShortDesignationName(designationName);
    acc[shortName] = (acc[shortName] || 0) + 1;
    return acc;
  }, {});

  const designationData = Object.entries(designationCounts).map(
    ([name, value]) => ({ name, value })
  );

  const ageGroupCounts = employees.reduce((acc, emp) => {
    const age = Number(emp.Age ?? emp.age);
    if (!Number.isFinite(age) || age < 0) return acc;
    if (age < 20) acc["Below 20"] = (acc["Below 20"] || 0) + 1;
    else if (age <= 25) acc["20-25"] = (acc["20-25"] || 0) + 1;
    else if (age <= 30) acc["26-30"] = (acc["26-30"] || 0) + 1;
    else if (age <= 35) acc["31-35"] = (acc["31-35"] || 0) + 1;
    else if (age <= 40) acc["36-40"] = (acc["36-40"] || 0) + 1;
    else acc["40+"] = (acc["40+"] || 0) + 1;
    return acc;
  }, {});

  const ageGroupData = [
    {
      id: "ageBelow20",
      value: ageGroupCounts["Below 20"] || 0,
      label: "Below 20",
      color: "var(--ems-chart-6)",
    },
    {
      id: "age20-25",
      value: ageGroupCounts["20-25"] || 0,
      label: "20-25",
      color: "var(--ems-chart-1)",
    },
    {
      id: "age26-30",
      value: ageGroupCounts["26-30"] || 0,
      label: "26-30",
      color: "var(--ems-chart-2)",
    },
    {
      id: "age31-35",
      value: ageGroupCounts["31-35"] || 0,
      label: "31-35",
      color: "var(--ems-chart-3)",
    },
    {
      id: "age36-40",
      value: ageGroupCounts["36-40"] || 0,
      label: "36-40",
      color: "var(--ems-chart-4)",
    },
    {
      id: "age40+",
      value: ageGroupCounts["40+"] || 0,
      label: "40+",
      color: "var(--ems-chart-5)",
    },
  ].filter((item) => item.value > 0);

  const experienceCounts = employees.reduce((acc, emp) => {
    if (emp.Date_of_Joining) {
      try {
        const joiningDate = new Date(emp.Date_of_Joining);
        if (!isNaN(joiningDate.getTime())) {
          const yearsOfExperience =
            currentDate.getFullYear() - joiningDate.getFullYear();

          if (yearsOfExperience >= 0 && yearsOfExperience <= 2)
            acc["0-2 years"] = (acc["0-2 years"] || 0) + 1;
          else if (yearsOfExperience >= 3 && yearsOfExperience <= 5)
            acc["3-5 years"] = (acc["3-5 years"] || 0) + 1;
          else if (yearsOfExperience >= 6 && yearsOfExperience <= 10)
            acc["6-10 years"] = (acc["6-10 years"] || 0) + 1;
          else if (yearsOfExperience > 10)
            acc["10+ years"] = (acc["10+ years"] || 0) + 1;
        }
      } catch (e) {
        console.error(
          `Error processing Date_of_Joining for employee: ${emp.id || emp.name || "Unknown ID"}.`,
          e
        );
      }
    }
    return acc;
  }, {});

  const experienceData = [
    { name: "0-2 years", value: experienceCounts["0-2 years"] || 0 },
    { name: "3-5 years", value: experienceCounts["3-5 years"] || 0 },
    { name: "6-10 years", value: experienceCounts["6-10 years"] || 0 },
    { name: "10+ years", value: experienceCounts["10+ years"] || 0 },
  ];

  const countByLabel = (people, pickLabel, emptyLabel) => {
    return (people || []).reduce((acc, emp) => {
      const raw = pickLabel(emp);
      const label =
        typeof raw === "string" && raw.trim() && raw.trim() !== "-"
          ? raw.trim()
          : emptyLabel;
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
  };

  const functionalRoleCounts = countByLabel(
    employees,
    (emp) =>
      emp.Functional_Role ||
      emp.functional_role ||
      emp.functional_role_name ||
      emp.functionRole?.functional_role_name,
    "No Functional Role"
  );

  const roleSpecializationCounts = countByLabel(
    employees,
    (emp) =>
      emp.Role_Specialization ||
      emp.role_specialization ||
      emp.functional_role_specialization ||
      emp.functional_role_specialization_name ||
      emp.RoleSpecialization?.functional_role_specialization_name,
    "No Specialization"
  );

  const functionalRoleData = Object.entries(functionalRoleCounts).map(
    ([name, value]) => ({ name, value })
  );
  const roleSpecializationData = Object.entries(roleSpecializationCounts).map(
    ([name, value]) => ({ name, value })
  );

  const allChartConfigs = [
    {
      id: "gender",
      title: "Distribution by Gender",
      icon: null,
      type: "pie",
      data: genderData,
      color: "#3B82F6",
      group: "Demographics",
    },
    {
      id: "ageGroup",
      title: "Distribution by Age Group",
      icon: null,
      type: "pie",
      data: ageGroupData,
      color: "#EF4444",
      group: "Demographics",
    },
    (() => {
      const sortedDepartmentData = departmentData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "department",
        title: "Distribution by Department",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedDepartmentData,
        yAxisData: sortedDepartmentData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDepartmentData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-1)",
          },
        ],
        color: "var(--ems-chart-1)",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedEmploymentTypeData = employmentTypeData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "employmentType",
        title: "Distribution by Employment Type",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedEmploymentTypeData,
        yAxisData: sortedEmploymentTypeData.map((d) => d.name),
        seriesData: [
          {
            data: sortedEmploymentTypeData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-2)",
          },
        ],
        color: "var(--ems-chart-2)",
        group: "Employment Details",
      };
    })(),
    (() => {
      const experienceOrder = [
        "0-2 years",
        "3-5 years",
        "6-10 years",
        "10+ years",
      ];
      const sortedExperienceData = experienceData.sort((a, b) => {
        return (
          experienceOrder.indexOf(a.name) - experienceOrder.indexOf(b.name)
        );
      });
      return {
        id: "experience",
        title: "Distribution by Experience Level",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedExperienceData,
        yAxisData: sortedExperienceData.map((d) => d.name),
        seriesData: [
          {
            data: sortedExperienceData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-3)",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "var(--ems-chart-3)",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedFunctionalRoleData = functionalRoleData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "functionalRole",
        title: "Distribution by Functional Role",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedFunctionalRoleData,
        yAxisData: sortedFunctionalRoleData.map((d) => d.name),
        seriesData: [
          {
            data: sortedFunctionalRoleData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-1)",
          },
        ],
        color: "var(--ems-chart-1)",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedRoleSpecializationData = roleSpecializationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "roleSpecialization",
        title: "Distribution by Role Specialization",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedRoleSpecializationData,
        yAxisData: sortedRoleSpecializationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedRoleSpecializationData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-5)",
          },
        ],
        color: "var(--ems-chart-5)",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedDesignationData = designationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "designation",
        title: "Distribution by Designation",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedDesignationData,
        yAxisData: sortedDesignationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDesignationData.map((d) => d.value),
            label: "Employees",
            color: "var(--ems-chart-4)",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "var(--ems-chart-4)",
        group: "Employment Details",
      };
    })(),
  ];



  // const allWidgetConfigs = [
  //   {
  //     id: "totalworkforce",
  //     title: "Total Workforce",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length - freelancers.length,
  //   },
  //  {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: employees.length   - (employees.length - freelancers.length),
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "newJoiningThisMonth",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalInterns",
  //     title: "Interns",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: interns.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthInterns",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: freelancers.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthFreelancers",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "birthdayThisMonth",
  //     title: "Birthday This Month",
  //     icon: null,
  //     color: "#EC4899",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Birth_Month=this_month`,
  //     value: calculateWidgetValues.birthday,
  //   },
  //   {
  //     id: "workAnniversariesThisMonth",
  //     title: "Work Anniversaries This Month",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Work_Anniversary=this_month`,
  //     value: calculateWidgetValues.anniversary,
  //   },

  //   {
  //     id: "presentToday",
  //     title: "Present Today",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Time & Attendance",
  //     value: attendanceStats.present,
  //   },
  //   {
  //     id: "absentToday",
  //     title: "Absent Today",
  //     icon: null,
  //     color: "#EF4444",
  //     group: "Time & Attendance",
  //     value: attendanceStats.absent,
  //   },
  //   {
  //     id: "lateCheckInsToday",
  //     title: "Late Check-ins Today",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Time & Attendance",
  //     value: attendanceStats.late,
  //   },

  //   {
  //     id: "onLeaveToday",
  //     title: "On Leave Today",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Leaves",
  //     value: leavesStats.onLeaveToday,
  //   },
  //   {
  //     id: "upcomingLeaves",
  //     title: "Upcoming Leaves (7d)",
  //     icon: null,
  //     color: "#8B5CF6",
  //     group: "Leaves",
  //     value: leavesStats.upcomingLeaves,
  //   },
  //   {
  //     id: "pendingLeaveRequests",
  //     title: "Pending Leave Requests",
  //     icon: null,
  //     color: "#F97316",
  //     group: "Leaves",
  //     value: leavesStats.pending,
  //   },
  // ];


  const allWidgetConfigs = [
    {
      id: "totalworkforce",
      title: "Total Workforce",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
      value: employees.length,
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length - freelancers.length,
    },
    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Total Workforce",
      link: `/organization/freelancer/freelancer-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelance&filter_employment_type_operator=equals`,
      value: employees.length - (employees.length - freelancers.length),
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length,
    },
    {
      id: "newJoiningThisMonth",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month`,
      value: calculateWidgetValues.newJoining,
    },

    {
      id: "totalInterns",
      title: "Interns",
      icon: null,
      color: "#10B981",
      group: "Interns",
      value: interns.length,
    },

    {
      id: "newJoiningThisMonthInterns",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Interns",
      link: `/organization/intern/intern-details?pageSize=10&sortBy=intern_code&sortOrder=desc&filter_internship_start_date=&filter_internship_start_date_operator=this_month`,
      value: calculateInternWidgetValues.newJoining,
    },
    {
      id: "birthdayThisMonthInterns",
      title: "Upcoming Birthdays (30d)",
      icon: null,
      color: "#EC4899",
      group: "Interns",
      link: `/organization/intern/intern-details?pageSize=10&sortBy=intern_code&sortOrder=desc&filter_date_of_birth=&filter_date_of_birth_operator=next_30_days`,
      value: calculateInternWidgetValues.birthday,
    },

    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/freelancer/freelancer-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelance&filter_employment_type_operator=equals`,
      value: freelancers.length,
    },

    {
      id: "newJoiningThisMonthFreelancers",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/freelancer/freelancer-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month&filter_employment_type=Freelance&filter_employment_type_operator=equals`,
      value: calculateFreelancerWidgetValues.newJoining,
    },

    {
      id: "birthdayThisMonth",
      title: "Upcoming Birthdays (30d)",
      icon: null,
      color: "#EC4899",
      group: "Occasions",
      link: `/organization/employee/employee-details?pageSize=10&sortBy=employee_code&sortOrder=desc&filter_date_of_birth=&filter_date_of_birth_operator=next_30_days&filter_employment_type=Employee&filter_employment_type_operator=contains`,
      value: calculateWidgetValues.birthday,
    },
    {
      id: "workAnniversariesThisMonth",
      title: "Upcoming Work Anniversaries (30d)",
      icon: null,
      color: "#F59E0B",
      group: "Occasions",
      link: `/organization/employee/employee-details?pageSize=10&sortBy=employee_code&sortOrder=desc&filter_date_of_joining=&filter_date_of_joining_operator=next_30_days_anniversary&filter_employment_type=Employee&filter_employment_type_operator=contains`,
      value: calculateWidgetValues.anniversary,
    },

    {
      id: "presentToday",
      title: "Present Today",
      icon: null,
      color: "#10B981",
      group: "Time & Attendance",
      value: attendanceStats.present,
    },
    {
      id: "absentToday",
      title: "Absent Today",
      icon: null,
      color: "#EF4444",
      group: "Time & Attendance",
      value: attendanceStats.absent,
    },
    {
      id: "lateCheckInsToday",
      title: "Late Check-ins Today",
      icon: null,
      color: "#F59E0B",
      group: "Time & Attendance",
      value: attendanceStats.late,
    },

    {
      id: "onLeaveToday",
      title: "On Leave Today",
      icon: null,
      color: "#3B82F6",
      group: "Leaves",
      value: leavesStats.onLeaveToday,
    },
    {
      id: "upcomingLeaves",
      title: "Upcoming Leaves (7d)",
      icon: null,
      color: "#8B5CF6",
      group: "Leaves",
      value: leavesStats.upcomingLeaves,
    },
    {
      id: "pendingLeaveRequests",
      title: "Pending Leave Requests",
      icon: null,
      color: "#F97316",
      group: "Leaves",
      value: leavesStats.pending,
    },
  ];

 

  const [visibleChartIds, setVisibleChartIds] = React.useState([]);
  const [visibleWidgetIds, setVisibleWidgetIds] = React.useState([]);
  const [chartViewModes, setChartViewModes] = React.useState({});

  React.useEffect(() => {
    const savedChartSettings = localStorage.getItem("chartDisplaySettings");
    const savedWidgetSettings = localStorage.getItem("widgetDisplaySettings");

    if (savedChartSettings) {
      const parsedSettings = JSON.parse(savedChartSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [
          s.id,
          { isVisible: !!s.isVisible, viewMode: s.viewMode || "chart" },
        ])
      );

      const mergedVisibleChartIds = allChartConfigs
        .filter((config) => {
          const saved = savedMap.get(config.id);
          return saved ? saved.isVisible : true;
        })
        .map((c) => c.id);

      setVisibleChartIds(mergedVisibleChartIds);

      const modes = {};
      allChartConfigs.forEach((chart) => {
        const saved = savedMap.get(chart.id);
        modes[chart.id] = saved?.viewMode || "chart";
      });
      setChartViewModes(modes);
    } else {
      setVisibleChartIds(allChartConfigs.map((config) => config.id));
      const modes = {};
      allChartConfigs.forEach((chart) => (modes[chart.id] = "chart"));
      setChartViewModes(modes);
    }

    if (savedWidgetSettings) {
      const parsedSettings = JSON.parse(savedWidgetSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [s.id, !!s.isVisible])
      );

      const mergedVisibleWidgetIds = allWidgetConfigs
        .filter((config) => {
          const savedVisible = savedMap.get(config.id);
          return savedVisible !== undefined ? savedVisible : true;
        })
        .map((w) => w.id);

      setVisibleWidgetIds(mergedVisibleWidgetIds);
    } else {
      setVisibleWidgetIds(allWidgetConfigs.map((config) => config.id));
    }
  }, []);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };


  const handleWidgetClick = (widgetId, widgetTitle) => {
    if (widgetId === "totalworkforce") {
      // Total Workforce summary card is display-only (not clickable).
      return;
    }
    if (widgetId === "newJoiningThisMonth") {
      navigate(
        "/organization/employee/employee-details?filter_date_of_joining=&filter_date_of_joining_operator=this_month&filter_employment_type=Employee&filter_employment_type_operator=equals",
        { state: { fromDashboard: true, widgetId, employmentType: "employee" } }
      );
      return;
    }
    if (widgetId === "totalEmployees") {
      navigate(
        "/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals",
        { state: { fromDashboard: true, widgetId, employmentType: "employee" } }
      );
      return;
    }
    if (widgetId === "totalFreelancers") {
      navigate(
        "/organization/freelancer/freelancer-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelance&filter_employment_type_operator=equals",
        { state: { fromDashboard: true, widgetId, employmentType: "freelancer" } }
      );
      return;
    }
    if (widgetId === "newJoiningThisMonthFreelancers") {
      navigate(
        "/organization/freelancer/freelancer-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month&filter_employment_type=Freelance&filter_employment_type_operator=equals",
        { state: { fromDashboard: true, widgetId, employmentType: "freelancer" } }
      );
      return;
    }
    if (widgetId === "newJoiningThisMonthInterns") {
      navigate(
        "/organization/intern/intern-details?pageSize=10&sortBy=intern_code&sortOrder=desc&filter_internship_start_date=&filter_internship_start_date_operator=this_month",
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "birthdayThisMonthInterns") {
      navigate(
        "/organization/intern/intern-details?pageSize=10&sortBy=intern_code&sortOrder=desc&filter_date_of_birth=&filter_date_of_birth_operator=next_30_days",
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "totalInterns") {
      navigate("/organization/intern/intern-details", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "birthdayThisMonth") {
      navigate(
        "/organization/employee/employee-details?pageSize=10&sortBy=employee_code&sortOrder=desc&filter_date_of_birth=&filter_date_of_birth_operator=next_30_days&filter_employment_type=Employee&filter_employment_type_operator=contains",
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "workAnniversariesThisMonth") {
      navigate(
        "/organization/employee/employee-details?pageSize=10&sortBy=employee_code&sortOrder=desc&filter_date_of_joining=&filter_date_of_joining_operator=next_30_days_anniversary&filter_employment_type=Employee&filter_employment_type_operator=contains",
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "presentToday" || widgetId === "absentToday" || widgetId === "lateCheckInsToday") {
      navigate(buildTodayClockInTimeLogsUrl("/attendance/time-logs"), {
        state: { fromDashboard: true, widgetId },
      });
      return;
    }
    if (widgetId === "onLeaveToday") {
      const todayYmd = formatYmd(new Date());
      navigate(
        buildLeaveListUrl(
          "/leave/employee-leaves",
          buildOnLeaveTodayFilters(todayYmd)
        ),
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "upcomingLeaves") {
      const { start, end } = getUpcomingLeaveRange(new Date());
      navigate(
        buildLeaveListUrl(
          "/leave/employee-leaves",
          buildUpcomingLeaveFilters(start, end)
        ),
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
    if (widgetId === "pendingLeaveRequests") {
      navigate(
        buildLeaveListUrl(
          "/leave/employee-leaves",
          buildPendingLeaveFilters()
        ),
        { state: { fromDashboard: true, widgetId } }
      );
      return;
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenPersonalizationPage = () => {
    navigate("dashboard/personalize-dashboard");
    handleCloseMenu();
  };

  const open = Boolean(anchorEl);

  const [attTab, setAttTab] = React.useState("employees");
  const [joinersTab, setJoinersTab] = React.useState("employees");
  const [todayAttTab, setTodayAttTab] = React.useState("employees");

  const employeeOnly = React.useMemo(() => {
    const todayYmd = localYmd(currentDate);
    return (employees || []).filter((e) => {
      const t = String(
        e?.Employment_Type || e?.employment_type || ""
      ).toLowerCase();
      if (t.includes("freelance")) return false;
      return isActiveWorkforcePerson(e, todayYmd);
    });
  }, [employees, currentDate]);

  const activeInterns = React.useMemo(() => {
    const todayYmd = localYmd(currentDate);
    return (interns || []).filter((p) => isActiveWorkforcePerson(p, todayYmd));
  }, [interns, currentDate]);

  const activeFreelancers = React.useMemo(() => {
    const todayYmd = localYmd(currentDate);
    return (freelancers || []).filter((p) =>
      isActiveWorkforcePerson(p, todayYmd)
    );
  }, [freelancers, currentDate]);

  const workforceCounts = React.useMemo(() => {
    return {
      total:
        employeeOnly.length + activeInterns.length + activeFreelancers.length,
      employees: employeeOnly.length,
      interns: activeInterns.length,
      freelancers: activeFreelancers.length,
    };
  }, [employeeOnly, activeInterns, activeFreelancers]);

  const greetingName =
    userData?.full_name ||
    userData?.name ||
    userData?.organizationuser?.full_name ||
    "there";

  const shortDateLabel = currentDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const leaveByType = React.useMemo(
    () => ({
      onLeave: {
        employees: leaveOverviewCounts.employees.onLeave,
        interns: leaveOverviewCounts.interns.onLeave,
        freelancers: leaveOverviewCounts.freelancers.onLeave,
      },
      upcoming: {
        employees: leaveOverviewCounts.employees.upcoming,
        interns: leaveOverviewCounts.interns.upcoming,
        freelancers: leaveOverviewCounts.freelancers.upcoming,
      },
      pending: {
        employees: leaveOverviewCounts.employees.pending,
        interns: leaveOverviewCounts.interns.pending,
        freelancers: leaveOverviewCounts.freelancers.pending,
      },
    }),
    [leaveOverviewCounts]
  );

  const openLeaveOverview = React.useCallback(
    (kind, personType = "employees") => {
      const basePath =
        personType === "interns"
          ? "/organization/intern/intern-leaves"
          : "/leave/employee-leaves";
      const todayYmd = formatYmd(new Date());
      const { start, end } = getUpcomingLeaveRange(new Date());
      const employmentTypeFilter =
        personType === "freelancers"
          ? {
              filter_employment_type: "Freelance",
              filter_employment_type_operator: "contains",
            }
          : personType === "employees"
            ? {
                filter_employment_type: "Freelance",
                filter_employment_type_operator: "does_not_contain",
              }
            : {};

      if (kind === "onLeave") {
        navigate(
          buildLeaveListUrl(basePath, {
            ...buildOnLeaveTodayFilters(todayYmd),
            ...employmentTypeFilter,
          }),
          { state: { fromDashboard: true, widgetId: "onLeaveToday" } }
        );
        return;
      }
      if (kind === "upcoming") {
        navigate(
          buildLeaveListUrl(basePath, {
            ...buildUpcomingLeaveFilters(start, end),
            ...employmentTypeFilter,
          }),
          { state: { fromDashboard: true, widgetId: "upcomingLeaves" } }
        );
        return;
      }
      navigate(
        buildLeaveListUrl(basePath, {
          ...buildPendingLeaveFilters(),
          ...employmentTypeFilter,
        }),
        { state: { fromDashboard: true, widgetId: "pendingLeaveRequests" } }
      );
    },
    [navigate]
  );

  const openOccasionList = React.useCallback(
    (kind, personType = "employees") => {
      const common = {
        page: "1",
        pageSize: "10",
        sortOrder: "desc",
      };

      const dateFilters =
        kind === "birthday"
          ? {
              filter_date_of_birth: "",
              filter_date_of_birth_operator: "next_30_days",
            }
          : {
              filter_date_of_joining: "",
              filter_date_of_joining_operator: "next_30_days_anniversary",
            };

      if (personType === "interns") {
        const params = new URLSearchParams({
          ...common,
          sortBy: "intern_code",
          ...dateFilters,
        });
        navigate(`/organization/intern/intern-details?${params.toString()}`, {
          state: {
            fromDashboard: true,
            widgetId:
              kind === "birthday"
                ? "birthdayThisMonthInterns"
                : "workAnniversariesThisMonthInterns",
          },
        });
        return;
      }

      if (personType === "freelancers") {
        const params = new URLSearchParams({
          ...common,
          sortBy: "employee_code",
          ...dateFilters,
          filter_employment_type: "Freelance",
          filter_employment_type_operator: "contains",
        });
        navigate(
          `/organization/freelancer/freelancer-details?${params.toString()}`,
          {
            state: {
              fromDashboard: true,
              widgetId:
                kind === "birthday"
                  ? "birthdayThisMonthFreelancers"
                  : "workAnniversariesThisMonthFreelancers",
              employmentType: "freelancer",
            },
          }
        );
        return;
      }

      // Employees — employment type must contain "Employee"
      const params = new URLSearchParams({
        ...common,
        sortBy: "employee_code",
        ...dateFilters,
        filter_employment_type: "Employee",
        filter_employment_type_operator: "contains",
      });
      navigate(
        `/organization/employee/employee-details?${params.toString()}`,
        {
          state: {
            fromDashboard: true,
            widgetId:
              kind === "birthday"
                ? "birthdayThisMonth"
                : "workAnniversariesThisMonth",
            employmentType: "employee",
          },
        }
      );
    },
    [navigate]
  );

  const employeesForOccasions = React.useMemo(
    () =>
      (employeeOnly || []).filter((person) =>
        isEmployeeEmploymentType(employmentTypeNameOf(person))
      ),
    [employeeOnly]
  );

  // Separate list-visible employee / intern / freelancer occasion counts (no mixing).
  const occasions = React.useMemo(() => {
    // Intern list hides Exited; birthday/anniversary counts must match that list.
    const listVisibleInterns = (activeInterns || []).filter(isInternVisibleOnInternList);
    const emp = countOccasionsForPeople(
      employeesForOccasions,
      parseBirthDate,
      parseJoiningDate,
      currentDate
    );
    const int = countOccasionsForPeople(
      listVisibleInterns,
      parseBirthDate,
      parseJoiningDate,
      currentDate
    );
    const free = countOccasionsForPeople(
      activeFreelancers,
      parseBirthDate,
      parseJoiningDate,
      currentDate
    );
    return {
      birthdays: {
        employees: emp.birthday,
        interns: int.birthday,
        freelancers: free.birthday,
      },
      anniversaries: {
        employees: emp.anniversary,
        interns: int.anniversary,
        freelancers: free.anniversary,
      },
    };
  }, [
    employeesForOccasions,
    activeInterns,
    activeFreelancers,
    parseBirthDate,
    parseJoiningDate,
    currentDate,
  ]);

  const occasionRangeLabel = React.useMemo(() => {
    const { start, end } = getUpcomingOccasionRange(currentDate);
    return formatOccasionRangeLabel(start, end);
  }, [currentDate]);

  const recentJoiners = React.useMemo(() => {
    const sixMonthsAgo = new Date(currentDate);
    sixMonthsAgo.setHours(0, 0, 0, 0);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const endOfToday = new Date(currentDate);
    endOfToday.setHours(23, 59, 59, 999);

    const recentWithinSixMonths = (list) =>
      [...list]
        .map((p) => {
          const joinRaw = personJoinDate(p);
          const joinDate = parseJoiningDate(joinRaw);
          return { person: p, joinDate };
        })
        .filter(({ joinDate }) => {
          if (!joinDate || Number.isNaN(joinDate.getTime())) return false;
          // Joined within the last 6 months (inclusive), not a future date
          return joinDate >= sixMonthsAgo && joinDate <= endOfToday;
        })
        .sort((a, b) => b.joinDate.getTime() - a.joinDate.getTime())
        .map((x) => x.person);

    return {
      employees: recentWithinSixMonths(employeeOnly),
      interns: recentWithinSixMonths(activeInterns),
      freelancers: recentWithinSixMonths(activeFreelancers),
    };
  }, [employeeOnly, activeInterns, activeFreelancers, parseJoiningDate, currentDate]);

  const todayAttendanceRows = React.useMemo(() => {
    const freelancerIds = new Set(
      (activeFreelancers || [])
        .map((p) => personOrgPersonId(p))
        .filter((id) => id != null)
        .map(String)
    );
    const freelancerCodes = new Set(
      (activeFreelancers || [])
        .map((p) =>
          String(p?.employee_code || p?.person_code || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );

    const isFreelancerLog = (log) => {
      const id = log?.organization_person_id;
      if (id != null && freelancerIds.has(String(id))) return true;
      const code = String(log?.employee_code || "")
        .trim()
        .toLowerCase();
      return Boolean(code && freelancerCodes.has(code));
    };

    const employeeLogs = filterLogsForPeople(
      (todayClockInLogs.employees || []).filter((log) => !isFreelancerLog(log)),
      employeeOnly
    );
    const freelancerLogs = filterLogsForPeople(
      (todayClockInLogs.employees || []).filter((log) => isFreelancerLog(log)),
      activeFreelancers
    );
    const internLogs = filterLogsForPeople(
      todayClockInLogs.interns || [],
      activeInterns
    );

    return {
      employees: mapClockInLogsToRows(employeeLogs, employeeOnly).slice(0, 10),
      interns: mapClockInLogsToRows(internLogs, activeInterns).slice(0, 10),
      freelancers: mapClockInLogsToRows(
        freelancerLogs,
        activeFreelancers
      ).slice(0, 10),
    };
  }, [todayClockInLogs, employeeOnly, activeInterns, activeFreelancers]);

  const todayAbsentRows = React.useMemo(() => {
    const shiftLookup = buildWorkShiftLookup(workShifts);
    const now = new Date();

    const empClockKeys = collectClockedInPersonKeys(
      filterLogsForPeople(todayClockInLogs.employees, [
        ...(employeeOnly || []),
        ...(activeFreelancers || []),
      ])
    );
    const intClockKeys = collectClockedInPersonKeys(
      filterLogsForPeople(todayClockInLogs.interns, activeInterns)
    );
    const empLeaveKeys = collectOnLeavePersonKeys(todayLeaveRows.employees);
    const intLeaveKeys = collectOnLeavePersonKeys(todayLeaveRows.interns);

    // Freelancers share employee leave + employee time-log APIs
    return {
      employees: buildAbsentRows({
        people: employeeOnly,
        shiftLookup,
        clockedKeys: empClockKeys,
        leaveKeys: empLeaveKeys,
        now,
        personType: "employees",
      }),
      interns: buildAbsentRows({
        people: activeInterns,
        shiftLookup,
        clockedKeys: intClockKeys,
        leaveKeys: intLeaveKeys,
        now,
        personType: "interns",
      }),
      freelancers: buildAbsentRows({
        people: activeFreelancers,
        shiftLookup,
        clockedKeys: empClockKeys,
        leaveKeys: empLeaveKeys,
        now,
        personType: "freelancers",
      }),
    };
  }, [
    workShifts,
    todayClockInLogs,
    todayLeaveRows,
    employeeOnly,
    activeInterns,
    activeFreelancers,
  ]);

  const openTodayAttendanceTimeLogs = React.useCallback(() => {
    if (todayAttTab === "interns") {
      navigate(buildTodayClockInTimeLogsUrl("/intern/attendance/time-logs"), {
        state: { fromDashboard: true, widgetId: "absentToday" },
      });
      return;
    }
    navigate(buildTodayClockInTimeLogsUrl("/attendance/time-logs"), {
      state: { fromDashboard: true, widgetId: "absentToday" },
    });
  }, [navigate, todayAttTab]);

  const openMarkAttendance = React.useCallback(
    (row, event) => {
      event?.stopPropagation?.();
      const personType = row?.personType || "employees";
      const rawId = row?.person
        ? personOrgPersonId(row.person)
        : row?.id != null && String(row.id) !== row?.name
          ? row.id
          : null;
      const organizationPersonId =
        rawId != null && String(rawId).trim() !== "" && !Number.isNaN(Number(rawId))
          ? Number(rawId)
          : rawId || "";
      const todayYmd = formatYmd(new Date());
      const state = {
        fromDashboard: true,
        widgetId: "markAttendance",
        prefill: {
          organization_person_id: organizationPersonId,
          attendance_date: todayYmd,
          attendance_log_type: "Clock In",
        },
      };
      if (personType === "interns") {
        navigate("/intern/attendance/time-logs/add", { state });
        return;
      }
      navigate("/attendance/time-logs/add", { state });
    },
    [navigate]
  );

  const openMarkLeave = React.useCallback(
    (row, event) => {
      event?.stopPropagation?.();
      const personType = row?.personType || "employees";
      const rawId = row?.person
        ? personOrgPersonId(row.person)
        : row?.id != null && String(row.id) !== row?.name
          ? row.id
          : null;
      const organizationPersonId =
        rawId != null && String(rawId).trim() !== "" && !Number.isNaN(Number(rawId))
          ? Number(rawId)
          : rawId || "";
      const todayYmd = formatYmd(new Date());
      const state = {
        fromDashboard: true,
        widgetId: "markLeave",
        prefill: {
          organization_person_id: organizationPersonId,
          leave_start_date: todayYmd,
          leave_end_date: todayYmd,
          leave_duration_type: "full_day",
          leave_status: "Pending",
          total_leave_days: 1,
        },
      };
      if (personType === "interns") {
        navigate("/organization/intern/intern-leaves/add", { state });
        return;
      }
      navigate("/leave/employee-leaves/add", { state });
    },
    [navigate]
  );
  const attendanceByTab = React.useMemo(() => {
    const freelancerIds = new Set(
      (activeFreelancers || [])
        .map((p) => personOrgPersonId(p))
        .filter((id) => id != null)
        .map(String)
    );
    const freelancerCodes = new Set(
      (activeFreelancers || [])
        .map((p) =>
          String(p?.employee_code || p?.person_code || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );
    const isFreelancerLog = (log) => {
      const id = log?.organization_person_id;
      if (id != null && freelancerIds.has(String(id))) return true;
      const code = String(log?.employee_code || "")
        .trim()
        .toLowerCase();
      return Boolean(code && freelancerCodes.has(code));
    };

    const shiftLookup = buildWorkShiftLookup(workShifts);
    const now = new Date();

    let people;
    let logs;
    let leaveKeys;
    if (attTab === "employees") {
      people = employeeOnly;
      logs = filterLogsForPeople(
        (todayClockInLogs.employees || []).filter((log) => !isFreelancerLog(log)),
        employeeOnly
      );
      leaveKeys = collectOnLeavePersonKeys(todayLeaveRows.employees);
    } else if (attTab === "interns") {
      people = activeInterns;
      logs = filterLogsForPeople(
        todayClockInLogs.interns || [],
        activeInterns
      );
      leaveKeys = collectOnLeavePersonKeys(todayLeaveRows.interns);
    } else {
      people = activeFreelancers;
      logs = filterLogsForPeople(
        (todayClockInLogs.employees || []).filter(isFreelancerLog),
        activeFreelancers
      );
      leaveKeys = collectOnLeavePersonKeys(todayLeaveRows.employees);
    }

    const clockedKeys = collectClockedInPersonKeys(logs);
    const clockRows = mapClockInLogsToRows(logs, people);
    const leave = (people || []).filter((p) =>
      keysIntersect(personMatchKeys(p), leaveKeys)
    ).length;
    // Clocked-in people (on time or late) who are not on leave today
    const present = clockRows.filter((r) => {
      const keys = personMatchKeys(r.person);
      return !keysIntersect(keys, leaveKeys);
    }).length;
    const absentRows = buildAbsentRows({
      people,
      shiftLookup,
      clockedKeys,
      leaveKeys,
      now,
      personType: attTab,
      limit: 0,
    }).filter((r) => r.status === "Absent");

    return {
      present,
      absent: absentRows.length,
      leave,
    };
  }, [
    attTab,
    workShifts,
    employeeOnly,
    activeInterns,
    activeFreelancers,
    todayClockInLogs,
    todayLeaveRows,
  ]);

  const attTotal =
    attendanceByTab.present + attendanceByTab.absent + attendanceByTab.leave || 1;
  const attDonutData = [
    { name: "Present", value: attendanceByTab.present, color: "#22c55e" },
    { name: "Absent", value: attendanceByTab.absent, color: "#ef4444" },
    { name: "Leave", value: attendanceByTab.leave, color: "#eab308" },
  ].filter((d) => d.value > 0);

  const attLegend = [
    { name: "Present", value: attendanceByTab.present, color: "#22c55e" },
    { name: "Absent", value: attendanceByTab.absent, color: "#ef4444" },
    { name: "Leave", value: attendanceByTab.leave, color: "#eab308" },
  ];

  const joinersList = recentJoiners[joinersTab] || [];
  const todayList = todayAbsentRows[todayAttTab] || [];

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="hd-head">
        <div>
          <h1 className="hd-greet">
            {greetForHour(currentDate)}, {greetingName}{" "}
            <span aria-hidden="true">👋</span>
          </h1>
          <p className="hd-sub">
            Here&apos;s an overview of your organization today.
          </p>
          </div>
        <div className="hd-head-actions">
          <div className="hd-date-chip">
            <CalendarDays size={15} />
            {shortDateLabel}
          </div>
          {Permission && Permission.includes("PERSONALIZE_DASHBOARD") && (
            <>
              <button
                type="button"
                className="hd-btn hd-btn-ghost"
                aria-controls={open ? "settings-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <Settings size={15} />
                Personalize
              </button>
              <Menu
                id="settings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{ "aria-labelledby": "settings-button" }}
              >
                <MenuItem onClick={handleOpenPersonalizationPage}>
                  <DashboardCustomize sx={{ mr: 1 }} />
                  Personalize Dashboard
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </div>

      <div className="hd-stats">
        <WorkforceStat
          tone="purple"
          icon={<Users size={20} />}
          label="Total Workforce"
          value={loading ? "—" : workforceCounts.total}
        />
        <WorkforceStat
          tone="teal"
          icon={<UserCheck size={20} />}
          label="Employees"
          value={loading ? "—" : workforceCounts.employees}
          onClick={() => handleWidgetClick("totalEmployees")}
        />
        <WorkforceStat
          tone="orange"
          icon={<GraduationCap size={20} />}
          label="Interns"
          value={loading ? "—" : workforceCounts.interns}
          onClick={() => handleWidgetClick("totalInterns")}
        />
        <WorkforceStat
          tone="blue"
          icon={<Briefcase size={20} />}
          label="Freelancers"
          value={loading ? "—" : workforceCounts.freelancers}
          onClick={() => handleWidgetClick("totalFreelancers")}
        />
      </div>

      <div className="hd-grid-3">
        {/* Attendance Overview */}
        <section className="hd-card">
          <div className="hd-card-head hd-card-head-stack">
            <h2 className="hd-card-title">Attendance Overview</h2>
            <div className="hd-tabs">
              {["employees", "interns", "freelancers"].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`hd-tab${attTab === t ? " on" : ""}`}
                  onClick={() => setAttTab(t)}
                >
                  {t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
              </div>
            </div>
          <div className="hd-att-pills">
            <span className="hd-mini-pill hd-mini-pill-present">
              P <strong>{attendanceByTab.present}</strong>
            </span>
            <span className="hd-mini-pill hd-mini-pill-absent">
              A <strong>{attendanceByTab.absent}</strong>
            </span>
            <span className="hd-mini-pill hd-mini-pill-leave">
              L <strong>{attendanceByTab.leave}</strong>
            </span>
          </div>
          <div className="hd-att-body">
            <div className="hd-donut-wrap">
              <div
                className="hd-donut-ring"
                style={{
                  background: attDonutData.length
                    ? `conic-gradient(${attDonutData
                        .reduce(
                          (acc, slice) => {
                            const start = acc.offset;
                            const end =
                              acc.offset + (slice.value / attTotal) * 100;
                            acc.offset = end;
                            acc.stops.push(
                              `${slice.color} ${start}% ${end}%`
                            );
                            return acc;
                          },
                          { offset: 0, stops: [] }
                        )
                        .stops.join(", ")})`
                    : "conic-gradient(#e8e9f4 0 100%)",
                }}
              />
              <div className="hd-donut-center">
                <div className="hd-donut-total">
                  {attendanceByTab.present +
                    attendanceByTab.absent +
                    attendanceByTab.leave}
                </div>
                <div className="hd-donut-label">Total</div>
              </div>
            </div>
            <div className="hd-legend">
              {attLegend.map((row) => {
                const pct =
                  attTotal > 0
                    ? Math.round((row.value / attTotal) * 100)
                    : 0;
                return (
                  <div key={row.name} className="hd-legend-row">
                    <div className="hd-legend-left">
                      <span
                        className="hd-dot"
                        title={row.name}
                        style={{ background: row.color }}
                      />
                      <span className="hd-legend-name">{row.name}</span>
                    </div>
                    <span className="hd-legend-count">{row.value}</span>
                    <span className="hd-legend-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Leave Overview */}
        <section className="hd-card">
          <div className="hd-card-head">
            <h2 className="hd-card-title">Leave Overview</h2>
          </div>
          <table className="hd-leave-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Employee</th>
                <th>Intern</th>
                <th>Freelancer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <button
                    type="button"
                    className="hd-leave-label hd-leave-nav"
                    onClick={() => openLeaveOverview("onLeave", "employees")}
                  >
                    <span className="hd-leave-ic blue">
                      <CalendarDays size={15} />
                    </span>
                    On Leave Today
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("onLeave", "employees")}
                  >
                    {leaveOverviewLoading ? "—" : leaveByType.onLeave.employees}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("onLeave", "interns")}
                  >
                    {leaveOverviewLoading ? "—" : leaveByType.onLeave.interns}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("onLeave", "freelancers")}
                  >
                    {leaveOverviewLoading
                      ? "—"
                      : leaveByType.onLeave.freelancers}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  <button
                    type="button"
                    className="hd-leave-label hd-leave-nav"
                    onClick={() => openLeaveOverview("upcoming", "employees")}
                  >
                    <span className="hd-leave-ic purple">
                      <Clock size={15} />
                    </span>
                    Upcoming Leaves (7d)
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("upcoming", "employees")}
                  >
                    {leaveOverviewLoading
                      ? "—"
                      : leaveByType.upcoming.employees}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("upcoming", "interns")}
                  >
                    {leaveOverviewLoading ? "—" : leaveByType.upcoming.interns}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("upcoming", "freelancers")}
                  >
                    {leaveOverviewLoading
                      ? "—"
                      : leaveByType.upcoming.freelancers}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  <button
                    type="button"
                    className="hd-leave-label hd-leave-nav"
                    onClick={() => openLeaveOverview("pending", "employees")}
                  >
                    <span className="hd-leave-ic orange">
                      <Sparkles size={15} />
                    </span>
                    Pending Requests
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("pending", "employees")}
                  >
                    {leaveOverviewLoading
                      ? "—"
                      : leaveByType.pending.employees}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("pending", "interns")}
                  >
                    {leaveOverviewLoading ? "—" : leaveByType.pending.interns}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="hd-leave-count"
                    onClick={() => openLeaveOverview("pending", "freelancers")}
                  >
                    {leaveOverviewLoading
                      ? "—"
                      : leaveByType.pending.freelancers}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Upcoming Occasions */}
        <section className="hd-card">
          <div className="hd-card-head">
            <h2 className="hd-card-title">Occasions</h2>
            <span className="hd-card-subtitle">Next 30 days · {occasionRangeLabel}</span>
          </div>
          <div className="hd-occasion">
            <div className="hd-occ-block">
              <div className="hd-occ-title">
                <Cake size={15} /> Upcoming Birthdays
              </div>
              <div className="hd-occ-meta">
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("birthday", "employees")}
                >
                  Employees <strong>{occasions.birthdays.employees}</strong>
                </button>
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("birthday", "interns")}
                >
                  Interns <strong>{occasions.birthdays.interns}</strong>
                </button>
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("birthday", "freelancers")}
                >
                  Freelancers <strong>{occasions.birthdays.freelancers}</strong>
                </button>
              </div>
            </div>
            <div className="hd-occ-block">
              <div className="hd-occ-title">
                <Award size={15} /> Upcoming Work Anniversaries
              </div>
              <div className="hd-occ-meta">
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("anniversary", "employees")}
                >
                  Employees <strong>{occasions.anniversaries.employees}</strong>
                </button>
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("anniversary", "interns")}
                >
                  Interns <strong>{occasions.anniversaries.interns}</strong>
                </button>
                <button
                  type="button"
                  className="hd-occ-link"
                  onClick={() => openOccasionList("anniversary", "freelancers")}
                >
                  Freelancers{" "}
                  <strong>{occasions.anniversaries.freelancers}</strong>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="hd-grid-3 hd-bottom-row">
        {/* Recent Joiners */}
        <section className="hd-card hd-card-panel">
          <div className="hd-card-head hd-card-head-stack">
            <h2 className="hd-card-title">Recent Joiners</h2>
            <div className="hd-tabs">
              {[
                { id: "employees", label: "Employees" },
                { id: "interns", label: "Interns" },
                { id: "freelancers", label: "Freelancers" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`hd-tab${joinersTab === t.id ? " on" : ""}`}
                  onClick={() => setJoinersTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {joinersList.length === 0 ? (
            <div className="hd-empty">No joiners in the last 6 months</div>
          ) : (
            <div className="hd-table-scroll">
              <table className="hd-people-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {joinersList.map((p, idx) => {
                    const name = personDisplayName(p);
                    const folder =
                      joinersTab === "interns"
                        ? "interns"
                        : joinersTab === "freelancers"
                          ? "employees"
                          : "employees";
                return (
                      <tr key={p.employee_id || p.intern_id || idx}>
                        <td>
                          <div className="hd-person">
                            <PersonAvatar
                              person={p}
                              name={name}
                              toneIndex={idx}
                              folder={folder}
                            />
                            <span className="hd-person-name" title={name}>
                              {name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="hd-cell-clip" title={personRole(p)}>
                            {personRole(p)}
                          </span>
                        </td>
                      </tr>
                );
              })}
                </tbody>
              </table>
            </div>
          )}
          <div className="hd-card-foot">
            <button
              type="button"
              className="hd-card-link"
              onClick={() => {
                if (joinersTab === "interns") handleWidgetClick("totalInterns");
                else if (joinersTab === "freelancers")
                  handleWidgetClick("totalFreelancers");
                else handleWidgetClick("totalEmployees");
              }}
            >
              View All{" "}
              {joinersTab === "interns"
                ? "Interns"
                : joinersTab === "freelancers"
                  ? "Freelancers"
                  : "Employees"}
            </button>
            </div>
          </section>

        {/* Today's Absentees (shift-based + on leave) */}
        <section className="hd-card hd-card-panel">
          <div className="hd-card-head hd-card-head-stack">
            <h2 className="hd-card-title">Today&apos;s Absentees</h2>
            <div className="hd-tabs">
              {[
                { id: "employees", label: "Employees" },
                { id: "interns", label: "Interns" },
                { id: "freelancers", label: "Freelancers" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`hd-tab${todayAttTab === t.id ? " on" : ""}`}
                  onClick={() => setTodayAttTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {todayAttLoading ? (
            <div className="hd-empty">Loading absentees…</div>
          ) : todayList.length === 0 ? (
            <div className="hd-empty">No absentees right now</div>
          ) : (
            <div className="hd-table-scroll hd-table-scroll-absent">
              <table className="hd-people-table hd-people-table-absent">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {todayList.map((row, idx) => {
                    const folder =
                      todayAttTab === "interns" ? "interns" : "employees";
                    return (
                      <tr key={`${row.id}-${idx}`}>
                        <td>
                          <div className="hd-person">
                            <PersonAvatar
                              person={row.person}
                              name={row.name}
                              toneIndex={idx}
                              folder={folder}
                            />
                            <span className="hd-person-name" title={row.name}>
                              {row.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`hd-status ${
                              row.status === "On Leave" ? "onleave" : "absent"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="hd-absent-action-cell">
                          {row.showMarkAttendance || row.showMarkLeave ? (
                            <div className="hd-absent-actions">
                              {row.showMarkAttendance ? (
                                <button
                                  type="button"
                                  className="hd-mark-att-btn"
                                  title="Mark Attendance"
                                  aria-label="Mark Attendance"
                                  onClick={(e) => openMarkAttendance(row, e)}
                                >
                                  <ClipboardCheck size={14} strokeWidth={2.25} />
                                </button>
                              ) : null}
                              {row.showMarkLeave ? (
                                <button
                                  type="button"
                                  className="hd-mark-leave-btn"
                                  title="Mark Leave"
                                  aria-label="Mark Leave"
                                  onClick={(e) => openMarkLeave(row, e)}
                                >
                                  <CalendarDays size={14} strokeWidth={2.25} />
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <span className="hd-action-none">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="hd-card-foot">
            <button
              type="button"
              className="hd-card-link"
              onClick={openTodayAttendanceTimeLogs}
            >
              View Full Attendance
            </button>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="hd-card hd-card-panel">
          <div className="hd-card-head">
            <h2 className="hd-card-title">Quick Actions</h2>
          </div>
          <div className="hd-actions">
            <button
              type="button"
              className="hd-action purple"
              onClick={() =>
                navigate("/organization/employee/employee-details/add")
              }
            >
              <span className="hd-action-ic">
                <UserPlus size={18} strokeWidth={2.2} />
              </span>
              Add Employee
            </button>
            <button
              type="button"
              className="hd-action orange"
              onClick={() => navigate("/organization/intern/intern-details/add")}
            >
              <span className="hd-action-ic">
                <GraduationCap size={18} strokeWidth={2.2} />
              </span>
              Add Intern
            </button>
            <button
              type="button"
              className="hd-action teal"
              onClick={() => navigate("/attendance/time-logs")}
            >
              <span className="hd-action-ic">
                <ClipboardCheck size={18} strokeWidth={2.2} />
              </span>
              Employee Attendance
            </button>
            <button
              type="button"
              className="hd-action blue"
              onClick={() => navigate("/intern/attendance/time-logs")}
            >
              <span className="hd-action-ic">
                <Timer size={18} strokeWidth={2.2} />
              </span>
              Intern Attendance
            </button>
          </div>
        </section>
      </div>

      {error && (
        <div className="hd-empty" style={{ color: "var(--ems-red)" }}>
          {error}
        </div>
      )}

      {allChartConfigs.some((chart) => visibleChartIds.includes(chart.id)) && (
        <section className="ems-monthly-charts-grid hd-analytics" style={{ marginTop: 24 }}>
          <div
            className="ems-sec-head"
            style={{ gridColumn: "1 / -1", marginBottom: 4 }}
          >
            <div className="ems-sec-title">
              <span className="ems-bar" />
                Employee Distribution Analytics
            </div>
          </div>
                {allChartConfigs
                  .filter((chart) => visibleChartIds.includes(chart.id))
            .map((chart) => {
              const pieRows = (chart.data || []).map((row, i) => ({
                name: row.label || row.name,
                value: Number(row.value) || 0,
                color: row.color || EMS_CHART_COLORS[i % EMS_CHART_COLORS.length],
              }));
              const showShortLabels = chart.id === "department";
              const barRows = (chart.data || []).map((row) => {
                const fullName = row.fullName || row.name || row.label;
                let name = row.name || row.label;
                if (
                  showShortLabels &&
                  (!name ||
                    String(name).length > 4 ||
                    /[\s&]/.test(String(name)))
                ) {
                  name = getDepartmentShortName(fullName, row.name || row.label);
                }
                return {
                  name,
                  fullName,
                  value: Number(row.value) || 0,
                };
              });
              const total = (chart.data || []).reduce(
                (sum, row) => sum + (Number(row.value) || 0),
                0
              );
              const hasData = total > 0;
              // Long role labels clip on angled X-axis; show them on Y-axis instead.
              const isHorizontal =
                chart.id === "functionalRole" ||
                chart.id === "roleSpecialization";
              const longestLabelLen = barRows.reduce(
                (max, row) => Math.max(max, String(row.name || "").length),
                0
              );
              const categoryAxisWidth = Math.min(
                180,
                Math.max(100, Math.round(longestLabelLen * 6.2) + 12)
              );

              return (
                <div key={chart.id} className="ems-card ems-chart-card">
                  <div className="ems-chart-head">
                    <div>
                      <div className="ems-sec-title" style={{ fontSize: 17 }}>
                        <span className="ems-bar" />
                        {chart.title}
                      </div>
                      <p className="ems-chart-desc">
                        {chart.group || "Distribution"} —{" "}
                        <strong>{total}</strong> total
                      </p>
                    </div>
                  </div>

                  <div style={{ width: "100%", height: CHART_PLOT_HEIGHT }}>
                    {!hasData ? (
                      <div className="ems-chart-empty">No data for this chart</div>
                    ) : chartViewModes[chart.id] !== "chart" ? (
                      <div className="ems-chart-table-wrap">
                              <DataGrid
                                rowBuffer={2}
                                rowThreshold={0}
                                sx={{
                            height: CHART_PLOT_HEIGHT,
                            border: "none",
                            "& .MuiDataGrid-main": { height: "100%" },
                          }}
                          rows={(chart.data || []).map((row, index) => ({
                                  id: `${chart.id}-${index}`,
                                  label: row.fullName || row.label || row.name,
                                  shortLabel: row.name || row.label,
                                  value: row.value,
                                }))}
                                columns={[
                                  {
                                    field: "label",
                                    headerName:
                                      chart.type === "pie" ? "Label" : "Name",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                  ...(showShortLabels
                                    ? [
                                        {
                                          field: "shortLabel",
                                          headerName: "Short",
                                          width: 90,
                                          headerAlign: "center",
                                          align: "center",
                                        },
                                      ]
                                    : []),
                                  {
                                    field: "value",
                                    headerName: "Value",
                                    type: "number",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                ]}
                                pageSizeOptions={[5, 10, 25]}
                                initialState={{
                                  pagination: {
                                    paginationModel: { pageSize: 5 },
                                  },
                                }}
                                disableRowSelectionOnClick
                              />
                      </div>
                    ) : chart.type === "pie" ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieRows}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={58}
                            outerRadius={96}
                            paddingAngle={2}
                            stroke="var(--ems-surface)"
                            strokeWidth={2}
                            label={({ percent }) =>
                              percent >= 0.08
                                ? `${Math.round(percent * 100)}%`
                                : ""
                            }
                          >
                            {pieRows.map((entry, i) => (
                              <Cell
                                key={`${chart.id}-cell-${i}`}
                                fill={entry.color}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            content={
                              <DashboardChartTooltip valueLabel="People" />
                            }
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{
                              fontSize: 12,
                              fontFamily: "Manrope, sans-serif",
                              color: "var(--ems-muted)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={barRows}
                          layout={isHorizontal ? "vertical" : "horizontal"}
                          margin={{
                            top: 8,
                            right: 16,
                            left: isHorizontal ? 4 : 12,
                            bottom: isHorizontal ? 8 : 40,
                          }}
                        >
                          <CartesianGrid {...CHART_GRID_PROPS} />
                          {isHorizontal ? (
                            <>
                              <XAxis
                                type="number"
                                allowDecimals={false}
                                tick={CHART_AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={categoryAxisWidth}
                                tick={CHART_AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tickFormatter={(value) => {
                                  const text = String(value || "");
                                  const maxChars = Math.max(
                                    14,
                                    Math.floor((categoryAxisWidth - 8) / 6.2)
                                  );
                                  return text.length > maxChars
                                    ? `${text.slice(0, maxChars - 1)}…`
                                    : text;
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <XAxis
                                dataKey="name"
                                interval={0}
                                tick={CHART_AXIS_TICK}
                                axisLine={{ stroke: "var(--ems-border)" }}
                                tickLine={{
                                  stroke: "var(--ems-border-strong)",
                                }}
                                angle={
                                  showShortLabels
                                    ? 0
                                    : barRows.length > 6
                                      ? -35
                                      : 0
                                }
                                textAnchor={
                                  !showShortLabels && barRows.length > 6
                                    ? "end"
                                    : "middle"
                                }
                                height={
                                  !showShortLabels && barRows.length > 6
                                    ? 72
                                    : 40
                                }
                                dx={
                                  !showShortLabels && barRows.length > 6
                                    ? -4
                                    : 0
                                }
                              />
                              <YAxis
                                allowDecimals={false}
                                tick={CHART_AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                width={40}
                              />
                            </>
                          )}
                          <RechartsTooltip
                            cursor={{ fill: "var(--ems-primary-tint)" }}
                            content={
                              <DashboardChartTooltip
                                valueLabel="Employees"
                                showShortLabels={showShortLabels}
                              />
                            }
                          />
                          <Bar
                            dataKey="value"
                            fill={chart.color || "var(--ems-chart-1)"}
                            radius={isHorizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]}
                            maxBarSize={42}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {hasData && (
                    <div className="ems-chart-foot">
                      <strong>{total.toLocaleString("en-IN")}</strong>{" "}
                      {chart.type === "pie" ? "people" : "employees"}
                    </div>
                  )}
                </div>
              );
            })}
        </section>
      )}
    </div>
  );
}
